<template>
  <div>
    <!--    Loader-->
    <loading-panel :loading="loading"></loading-panel>

    <!--  Feature not enabled-->
    <div v-if="feature_not_enabled" class="mt-3" >
      <div class="font-icon-detail">
            <span style="font-size: 3em">
              <i class="fas fa-exclamation-triangle"></i>
            </span>
        <h4 class="mt-3">This feature is unavailable.</h4>
        <base-button type="primary" v-if="hasPermissions('billing_view')"
                     v-on:click="goToBilling()" class="mt-3">Go to billing</base-button>
        <p style="font-size: medium" v-else>Contact a site administrator to update billing.</p>
      </div>
    </div>

    <div v-if="!feature_not_enabled">
      <!--  Take a photo button-->
      <div class="row align-items-center mb-4">
        <div class="col d-sm-block d-none">
      <span class="btn btn-primary btn-file tp-take-photo-btn mt-3" @click="onTakePhotoClick"><i class="tim-icons icon-camera-18"></i>Take photo
              <input v-if="!useLiveCamera && isAndroid14" type="file"
                     @change="takePhoto()"/>
                <input v-else-if="!useLiveCamera" type="file"
                       accept="image/*"
                       @change="takePhoto()"

                />
              </span>
        </div>

        <div class="col-auto d-sm-none">
      <span class="btn btn-primary btn-icon btn-file mt-3" @click="onTakePhotoClick"><i style="font-size: larger" class="tim-icons icon-camera-18"></i>
              <input v-if="!useLiveCamera && isAndroid14" type="file"
                     @change="takePhoto()"/>
                <input v-else-if="!useLiveCamera" type="file"
                       accept="image/*"
                       @change="takePhoto()"

                />
              </span>
        </div>

        <div class="col col-sm-8 col-lg-auto mt-3 ml-auto">
          <div class="tp-date-nav-wrap">
            <base-date-navigator v-model="current" @change="changeDate()">
              <template #actions>
                <!-- The span keeps the tooltip alive while the button is disabled
                     (a disabled button fires no mouse events). -->
                <el-tooltip :content="downloadAllTooltip" effect="light" :open-delay="300" placement="top">
                  <span class="d-inline-flex">
                    <button type="button"
                            class="date-navigator__action-btn"
                            :disabled="!zipState.running && !canDownloadAll"
                            :aria-label="downloadAllTooltip"
                            aria-live="polite"
                            @click="downloadAllPhotos()">
                      <i v-if="zipState.running" class="el-icon-loading"></i>
                      <i v-else class="tim-icons icon-cloud-download-93"></i>
                      <span v-if="zipState.running">{{zipState.done}} of {{zipState.total}}</span>
                    </button>
                  </span>
                </el-tooltip>
              </template>
            </base-date-navigator>
          </div>
        </div>
      </div>
      <info-hint v-if="!email_active" variant="warning" icon="icon-email-85" class="mb-4">
        The photo email type is not activated, so photos cannot be emailed. Activate it in the mail settings.
      </info-hint>

      <!--  Photos-->
      <div class="row">
        <div style="word-break: break-all; display: flex" class="col-lg-4 col-md-6 col-12" v-for="(p, i) in photos" :key="'A'+ i" >
          <card >
            <div class="row justify-content-center" >
              <div class="col text-center">
                <div style="word-break: break-all;" >
                  <div style="position: relative; display: inline-block;" :id="'editorContainer' + i"
                       :style="p.isSizing ? {minWidth: '140px', minHeight: '140px'} : null">
                    <!-- Shown while a photo is loading into a reused <img> node and
                         being measured, so the resize isn't visible as a flash. -->
                    <div v-if="p.isSizing && !p.isProcessing" class="tp-photo-loader">
                      <svg class="custom-spinner" viewBox="0 0 50 50" style="width: 36px; height: 36px;">
                        <circle class="path" cx="25" cy="25" r="20" fill="none" stroke-width="5" />
                      </svg>
                    </div>

                    <div v-if="p.uploading || p.deleting" class="tp-photo-loader" style="z-index: 10">
                      <svg class="custom-spinner" viewBox="0 0 50 50" style="width: 36px; height: 36px;">
                        <circle class="path" cx="25" cy="25" r="20" fill="none" stroke-width="5" />
                      </svg>
                    </div>

                    <div v-if="p.isProcessing"
                         style="position: absolute; top: 0; left: 0; right: 0; bottom: 0;
                          background-color: rgba(0, 0, 0, 0.7); z-index: 10;
                          display: flex; align-items: center; justify-content: center;
                          border-radius: 0px;">
                      <div style="text-align: center; color: white;">
                        <svg class="custom-spinner" viewBox="0 0 50 50" style="width: 36px; height: 36px; margin-bottom: 10px;">
                      <circle class="path" cx="25" cy="25" r="20" fill="none" stroke-width="5" />
                      </svg>
                        <div style="font-size: 14px;">Processing overlay...</div>
                      </div>
                    </div>

                    <img v-if="p.overlay_url" :id="'img' + i" :src="p.overlay_url" @load="updateCanvasSize(i, p); loadPhotoSize(p)"
                         @error="photoLoadFailed(p)" crossorigin="anonymous"
                         style="max-width: 100%; border-radius: 0px"
                         :style="{visibility: p.isSizing ? 'hidden' : 'visible'}" >
                    <img v-else :id="'img' + i" :src="p.url" @load="updateCanvasSize(i, p); loadPhotoSize(p)"
                         @error="photoLoadFailed(p)" crossorigin="anonymous"
                         style="max-width: 100%; border-radius: 0px"
                         :style="{visibility: p.isSizing ? 'hidden' : 'visible'}" >
                    <canvas :id="'cv' + i" style="position: absolute; top: 0; left: 0;"
                            :style="{visibility: p.isSizing ? 'hidden' : 'visible'}"></canvas>
                  </div>
                </div>
              </div>
            </div>
            <div class="row mt-2">
              <div class="col">
                <h4 class="card-title">
                  {{getDayOfWeek(p.timestamp)}} {{getDate(p.timestamp)}}, {{getTime(p.timestamp)}}
                  <small v-if="p.size_mb" class="text-white tp-row-sub">· {{p.size_mb}} MB</small>
                  <template v-if="!p.uploading">
                  <el-tooltip content="Delete photo" style="margin-top: 0"
                              effect="light"
                              :open-delay="300"
                              placement="top">
                    <base-button
                      @click="deletePhoto(p)"
                      class="like btn-link"
                      type="danger"
                      size="sm"
                      icon
                    ><i class="tim-icons icon-trash-simple"></i>
                    </base-button>
                  </el-tooltip>
                  <el-tooltip content="Download photo" style="margin-top: 0"
                              effect="light"
                              :open-delay="300"
                              placement="top">
                    <base-button
                      @click="saveImage(p)"
                      class="like btn-link"
                      size="sm"
                      icon
                    ><i class="tim-icons text-white icon-cloud-download-93"></i>
                    </base-button>
                  </el-tooltip>
                  </template>
                </h4>
              </div>
            </div>

            <!-- Skeleton stand-ins for the action rows while the upload finishes,
                 so the card is not a tall blank while its neighbors have content. -->
            <div v-if="p.uploading" class="tp-upload-skel">
              <div class="skel tp-upload-skel__pill"></div>
              <div class="skel tp-upload-skel__label"></div>
              <div class="skel tp-upload-skel__select"></div>
            </div>

            <div class="row" v-if="!p.uploading">
              <div class="col">
                <base-button variant="outlined" tone="primary" size="sm" @click="openAssignmentDrawer(p)">
                  <i class="tim-icons icon-link-72"></i>Assign photo
                </base-button>
              </div>
            </div>
            <div class="tp-assignee-list" v-if="p.assignees && p.assignees.length > 0">
              <div v-for="(a, aidx) in p.assignees" :key="'A' + i + 'x' + aidx" class="tp-assignee-row">
                <div class="tp-picker-event">
                  <div class="tp-picker-event__thumb-wrap">
                    <img v-if="a.hasOwnProperty('booking_slot_id') && a.game_img"
                         v-twin-src="a.game_img" class="tp-picker-event__img" alt="" />
                    <div v-else-if="a.hasOwnProperty('booking_slot_id')"
                         class="tp-picker-event__img tp-picker-event__img--placeholder"></div>
                    <div v-else class="tp-participant-avatar">{{participantInitials(a)}}</div>
                  </div>
                  <div class="tp-picker-event__text">
                    <div v-if="a.hasOwnProperty('booking_slot_id')">
                      <div class="text-white tp-row-title">{{a.game_internal_name}}</div>
                      <small v-if="isPublicChild(a)" class="text-white tp-row-sub">
                        {{a.purchaser_first_name}} {{a.purchaser_last_name}}<span v-if="a.order_number"> · #{{a.order_number}}</span> · {{getDayOfWeek(a.booking_date)}} {{getDate(a.booking_date)}}, {{getTime(a.start_time)}}
                      </small>
                      <small v-else-if="isPublicMaster(a)" class="text-white tp-row-sub">
                        All parties on public slot · {{a.child_count}} parties · {{getDayOfWeek(a.booking_date)}} {{getDate(a.booking_date)}}, {{getTime(a.start_time)}}
                      </small>
                      <small v-else class="text-white tp-row-sub">
                        {{getDayOfWeek(a.booking_date)}} {{getDate(a.booking_date)}}, {{getTime(a.start_time)}}
                      </small>
                    </div>
                    <div v-else-if="a.hasOwnProperty('user_id')">
                      <div class="text-white tp-row-title">{{a.first_name}} {{a.last_name}}</div>
                      <small class="text-white tp-row-sub">{{a.email}}</small>
                    </div>
                  </div>
                  <div class="tp-assignee-row__status" v-if="email_active == 1">
                    <badge v-if="a.hasOwnProperty('booking_slot_id') && a.emailed_to_group" type="success" class="tp-badge-sm">Sent x{{a.emailed_to_group}}</badge>
                    <badge v-else-if="a.hasOwnProperty('booking_slot_id') && !a.emailed_to_group" type="default" class="tp-badge-sm">Not sent</badge>
                    <badge v-else-if="a.hasOwnProperty('user_id') && a.is_emailed" type="success" class="tp-badge-sm">Sent x{{a.is_emailed}}</badge>
                    <badge v-else type="default" class="tp-badge-sm">Not sent</badge>
                  </div>
                  <el-dropdown trigger="click" class="tp-assignee-row__actions">
                    <button type="button" class="tp-assignee-row__kebab" aria-label="Actions">
                      <i class="tim-icons el-icon-more"></i>
                    </button>
                    <template #dropdown>
                      <el-dropdown-menu>
                        <a class="dropdown-item mb-2" @click.prevent="emailToGroupPrompt(a)">
                          <i class="tim-icons text-default icon-email-85 mr-2"></i> {{emailButtonLabel(a)}}
                          <div v-if="!email_active">
                            <small class="font-italic ml-4">Please activate the photo email type in email settings</small>
                          </div>
                        </a>
                        <a v-if="a.hasOwnProperty('booking_slot_id') && a.game_instance_id && hasPermissions('game_history_view')"
                           class="dropdown-item mb-2" @click.prevent="viewGameDetails(a)">
                          <i class="tim-icons text-default icon-puzzle-10 mr-2"></i> View event details
                        </a>
                        <a v-if="a.hasOwnProperty('booking_slot_id')" class="dropdown-item mb-2" @click.prevent="editTeam(a)">
                          <i class="tim-icons text-default icon-satisfied mr-2"></i> Manage team name
                        </a>
                        <hr>
                        <a v-if="a.hasOwnProperty('booking_slot_id')"
                           class="dropdown-item" @click.prevent="removeAssignment(a.photo_id, a.id, 1)">
                          <i class="tim-icons text-default icon-trash-simple mr-2"></i> Remove assignment
                        </a>
                        <a v-if="a.hasOwnProperty('user_id')" class="dropdown-item"
                           @click.prevent="removeAssignment(a.photo_id, a.id, 0)">
                          <i class="tim-icons text-default icon-trash-simple mr-2"></i> Remove assignment
                        </a>
                      </el-dropdown-menu>
                    </template>
                  </el-dropdown>
                </div>
              </div>
            </div>

            <!-- Overlay: the select is the state (None clears), Adjust is the one action -->
            <div class="tp-overlay-row" v-if="overlays.length > 0 && !p.uploading">
              <base-input label="Overlay" class="tp-overlay-field mb-0">
                <el-select
                  v-model="p.selectedOverlay"
                  class="select-primary"
                  name="overlay"
                  placeholder="No overlay applied"
                  :disabled="p.isProcessing"
                  v-on:change="onOverlayPicked(p, i)"
                  value-key="id"
                >
                  <el-option
                    v-for="o in overlays"
                    class="select-primary"
                    :label="o.name + ' [' + o.aspect_ratio +']'"
                    :value="{id: o.id, name: o.name, layers: o.layers,
                  aspect_ratio: o.aspect_ratio, reference_height: o.reference_height,
                  reference_width: o.reference_width, crop_to_overlay: o.crop_to_overlay}"
                    :key="o.id"
                  ></el-option>
                </el-select>
              </base-input>
              <div class="tp-overlay-actions" v-if="p.selectedOverlay">
                <el-tooltip content="Adjust overlay" style="margin-top: 0" effect="light" :open-delay="300" placement="top">
                  <base-button class="like btn-link tp-overlay-action" size="sm" icon :disabled="p.isProcessing" @click="openAdjust(p, i)">
                    <i class="tim-icons text-white icon-pencil"></i>
                  </base-button>
                </el-tooltip>
                <el-tooltip content="Clear overlay" style="margin-top: 0" effect="light" :open-delay="300" placement="top">
                  <base-button class="like btn-link tp-overlay-action" size="sm" icon :disabled="p.isProcessing" @click="onClearPicked(p, i)">
                    <i class="tim-icons text-white icon-simple-remove"></i>
                  </base-button>
                </el-tooltip>
              </div>
            </div>
          </card>
        </div>
      </div>




      <!--   Skeleton cards while the day's photos load -->
      <div class="row" v-if="skeletonsVisible && photos.length <= 0">
        <div class="col-lg-4 col-md-6 col-12" v-for="n in 3" :key="'SK' + n">
          <card>
            <div class="skel tp-skel-photo"></div>
            <div class="skel mt-3" style="width: 55%; height: 14px"></div>
            <div class="skel skel--line-sm mt-2" style="width: 35%"></div>
          </card>
        </div>
      </div>

      <!--   No data panel -->
      <div v-if="photos.length <= 0 && !photosLoading" class="text-center mt-4">
        <img src="/img/pandas/photo.png" alt="No photos" class="empty-state-img" />
        <h4 class="empty-state-text">No photos taken yet today</h4>
      </div>


      <!--    Email to group drawer -->
      <base-drawer :show="modals.email_group"
                   :title="emailGroupTitle"
                   @close="modals.email_group = false">

        <template #title-prefix>
          <img v-if="selectedPhoto && selectedPhoto.game_img"
               v-twin-src="selectedPhoto.game_img"
               class="tp-drawer-photo-ref__thumb"
               alt="" />
          <div v-else-if="selectedPhoto && selectedPhoto.hasOwnProperty('user_id')" class="tp-participant-avatar tp-drawer-photo-ref__avatar">
            {{participantInitials(selectedPhoto)}}
          </div>
          <div v-else class="tp-drawer-photo-ref__thumb tp-drawer-photo-ref__thumb--placeholder"></div>
        </template>

        <!-- Photo candidates -->
        <drawer-section v-if="emailPhotoCandidates.length > 0" title="Photos" icon="icon-camera-18">
          <div class="tp-photo-thumbs">
            <div v-for="(ep, epIndex) in emailPhotoCandidates" :key="'EP' + epIndex"
                 class="tp-photo-thumb"
                 :class="{'tp-photo-thumb--selected': ep.selected}">
              <img :src="ep.overlay_url || ep.url" crossorigin="anonymous" />
              <badge v-if="ep.size_mb" type="default" class="tp-photo-thumb__size">{{ep.size_mb}} MB</badge>
              <div class="tp-photo-thumb__check"
                   :class="{'tp-photo-thumb__check--selected': ep.selected}"
                   @click="$set(emailPhotoCandidates[epIndex], 'selected', !ep.selected)">
                <i v-if="ep.selected" class="tim-icons icon-check-2"></i>
              </div>
            </div>
          </div>
          <small class="mt-2 d-block text-white" style="opacity: 0.6">
            {{emailPhotoCandidates.filter(p => p.selected).length}} of {{emailPhotoCandidates.length}} photos selected<span v-if="selectedEmailMb"> · about {{selectedEmailMb}} MB as an email</span>
          </small>
          <info-hint v-if="selectedEmailOverBudget" variant="warning" class="mt-2">
            Over the 18 MB email limit. Photos that do not fit are sent as download links instead of attachments.
          </info-hint>
        </drawer-section>

        <!-- Recipients (waiver list) -->
        <drawer-section title="Recipients" icon="icon-email-85">
          <div v-if="emailUsersLoading">
            <div v-for="n in 3" :key="'EUSK' + n" class="d-flex align-items-center mb-2" style="gap: 12px">
              <span class="skel skel--dot"></span>
              <div style="flex: 1">
                <div class="skel" style="width: 60%; height: 14px; margin-bottom: 6px"></div>
                <div class="skel skel--line-sm"></div>
              </div>
            </div>
          </div>
          <div v-else-if="end_users.length <= 0" class="text-center py-3">
            <span style="font-size: 2em"><i class="tim-icons icon-single-02"></i></span>
            <h4 class="mt-2 text-white" style="font-size: 14px">No waivers signed yet</h4>
            <small class="text-white" style="opacity: 0.6">Use "Add another person" below to send to a specific customer</small>
          </div>
          <div v-else class="tp-picker-list">
            <div v-for="(eu, euIdx) in end_users" :key="'EU' + euIdx"
                 :class="['tp-picker-row', eu.selected ? 'tp-picker-row--selected' : '']"
                 @click="toggleRecipient(eu)">
              <div class="tp-picker-event">
                <div class="tp-picker-event__thumb-wrap" :class="{'is-selected': eu.selected}">
                  <div class="tp-participant-avatar">{{participantInitials(eu)}}</div>
                  <div class="tp-picker-event__check" :class="{'is-checked': eu.selected}">
                    <i class="tim-icons icon-check-2"></i>
                  </div>
                </div>
                <div class="tp-picker-event__text">
                  <div class="text-white tp-row-title">
                    {{eu.first_name}} {{eu.last_name}}
                    <badge v-if="eu.is_purchaser || (emailGroupPurchaserEmail && eu.email === emailGroupPurchaserEmail)"
                           type="primary" class="tp-badge-sm" style="margin-left: 6px">Purchaser</badge>
                    <badge v-else-if="eu.from_waiver"
                           type="default" class="tp-badge-sm" style="margin-left: 6px">Waiver</badge>
                    <badge v-if="eu.opted_out_photo_sharing"
                           type="danger" style="margin-left: 6px; font-size: 10px">Opted out</badge>
                  </div>
                  <small class="text-white tp-row-sub">
                    {{eu.email}}<span v-if="eu.order_number"> · #{{eu.order_number}}</span><span v-if="eu.purchaser_first_name"> · {{eu.purchaser_first_name}} {{eu.purchaser_last_name}}</span>
                  </small>
                </div>
              </div>
            </div>
          </div>
        </drawer-section>

        <!-- Add another person -->
        <drawer-section title="Add another person" icon="icon-single-02">
          <div v-if="!add_another_player">
            <base-button size="sm" type="primary" @click="add_another_player = true">
              <i class="tim-icons el-icon-plus"></i> Add another person
            </base-button>
          </div>
          <div v-else>
            <base-input label="Customer lookup (search by name, email, or phone)">
              <el-autocomplete style="width: 100%"
                               v-model="lookup_value"
                               :fetch-suggestions="querySearch"
                               placeholder="Search"
                               :trigger-on-focus="false"
                               @select="handleSelect">
                <template #default="{ item }">
                  <p style="margin-bottom: -12px" class="text-primary">{{ item.first_name }} {{ item.last_name }}</p>
                  <small class="font-italic">
                    <i class="tim-icons icon-email-85"></i> {{item.email }} &nbsp;
                    <i v-if="item.phone" class="tim-icons el-icon-mobile-phone"></i> {{item.phone }}
                  </small>
                </template>
              </el-autocomplete>
            </base-input>
            <base-button size="sm" type="default" @click="cancelAddAnotherPlayer()">Cancel</base-button>
            <base-button size="sm" type="primary" class="ml-2" @click="addCustomerWaiver()">Add to booking</base-button>
          </div>
        </drawer-section>

        <template #footer>
          <div class="footer-actions">
            <base-button size="sm" type="secondary" @click="modals.email_group = false">Cancel</base-button>
            <base-button size="sm" type="primary"
                         :disabled="sendingEmail || emailUsersLoading || (emailPhotoCandidates.length > 1 && emailPhotoCandidates.filter(p => p.selected).length === 0)"
                         @click="emailToGroup()">
              <span v-if="sendingEmail">Sending...</span>
              <span v-else>Send email</span>
            </base-button>
          </div>
        </template>
      </base-drawer>

      <!--    Email photos to customer Modal-->
      <modal :show.sync="modals.email_customer_photos"
             body-classes="p-0"
             modal-classes="modal-dialog-centered modal-sm">
        <card card-body-classes="table-full-width"
              header-classes="bg-white pb-5"
              body-classes="px-lg-5 py-lg-5"
              class="border-0 mb-0">

          <h4>Email photos to customer</h4>

          <div v-if="emailPhotoCandidates.length > 0" style="margin-bottom: 16px">
            <div class="tp-photo-thumbs">
              <div v-for="(ep, epIndex) in emailPhotoCandidates" :key="'EPC' + epIndex"
                   class="tp-photo-thumb"
                   :class="{'tp-photo-thumb--selected': ep.selected}">
                <img :src="ep.overlay_url || ep.url" crossorigin="anonymous" />
                <badge v-if="ep.size_mb" type="default" class="tp-photo-thumb__size">{{ep.size_mb}} MB</badge>
                <div class="tp-photo-thumb__check"
                     :class="{'tp-photo-thumb__check--selected': ep.selected}"
                     @click="$set(emailPhotoCandidates[epIndex], 'selected', !ep.selected)">
                  <i v-if="ep.selected" class="tim-icons icon-check-2"></i>
                </div>
              </div>
            </div>
            <span class="text-white" style="font-size: 11px; opacity: 0.6">
              {{emailPhotoCandidates.filter(p => p.selected).length}} of {{emailPhotoCandidates.length}} photos selected<span v-if="selectedEmailMb"> · about {{selectedEmailMb}} MB as an email</span>
            </span>
            <info-hint v-if="selectedEmailOverBudget" variant="warning" class="mt-2">
              Over the 18 MB email limit. Photos that do not fit are sent as download links instead of attachments.
            </info-hint>
          </div>

          <div v-if="emailCustomerTarget" style="margin-bottom: 16px">
            <p class="text-white" style="margin-bottom: 2px">
              Sending to: <strong>{{emailCustomerTarget.first_name}} {{emailCustomerTarget.last_name}}</strong>
            </p>
            <small class="text-white" style="opacity: 0.6">{{emailCustomerTarget.email}}</small>
          </div>

          <div class="row justify-content-center mt-3">
            <div class="col text-center">
              <base-button type="secondary" @click="modals.email_customer_photos = false">Cancel</base-button>
              <base-button class="ml-3" type="primary"
                           :disabled="sendingEmail || emailPhotoCandidates.filter(p => p.selected).length === 0"
                           @click="emailCustomerPhotos()">
                <span v-if="sendingEmail">Sending...</span>
                <span v-else>Send email</span>
              </base-button>
            </div>
          </div>
        </card>
      </modal>


      <!--    Manage team-->
      <modal :show.sync="modals.edit_team"
             body-classes="p-0"
             modal-classes="modal-dialog-centered modal-sm">
        <card card-body-classes="table-full-width"
              header-classes="bg-white pb-5"
              body-classes="px-lg-5 py-lg-5"
              class="border-0 mb-0">

          <h4>Manage team name</h4>
          <div class="row">
            <div class="col">
              <base-input label="Team name" v-model="selectedTeam.team_name"></base-input>
            </div>
          </div>

          <div class="row">
            <div class="col">
              <base-button type="default" v-on:click="modals.edit_team = false">Cancel</base-button>
              <base-button class="ml-2" type="primary" v-on:click="saveTeamName()">Save</base-button>
            </div>
          </div>
          <div class="row d-block d-sm-none text-center mt-3">
            <base-button simple size="sm" type="secondary" @click="modals.edit_team = false">Cancel</base-button>
          </div>
        </card>
      </modal>
    </div>

  <base-drawer :show="drawerOpen" title="Assign photo" @close="closeAssignmentDrawer">
    <template #title-prefix>
      <img v-if="drawerPhoto"
           :src="drawerPhoto.overlay_url || drawerPhoto.url"
           class="tp-drawer-photo-ref__thumb"
           crossorigin="anonymous" />
    </template>

    <!-- Mode selector -->
    <div class="mb-3 tp-mode-toggle">
      <segmented-toggle
        size="sm"
        :value="drawerMode"
        :options="drawerModeOptions"
        @input="onDrawerModeChange"
      />
    </div>

    <!-- Date navigator (hidden in Participants mode, which has no date concept) -->
    <base-date-navigator
      v-if="drawerMode !== 'players'"
      v-model="current_booking"
      class="mb-3"
      @change="onDrawerDateChange"
    />

    <!-- Party mode (was select_booking_slot modal) -->
    <drawer-section v-if="drawerMode === 'party'" title="Bookings" icon="icon-calendar-60">
      <div v-if="drawerLoading">
        <div v-for="n in 3" :key="'plp' + n" class="d-flex align-items-center mb-2" style="gap: 12px">
          <span class="skel skel--dot"></span>
          <div style="flex: 1">
            <div class="skel" style="width: 60%; height: 14px; margin-bottom: 6px"></div>
            <div class="skel skel--line-sm"></div>
          </div>
          <span class="skel skel--badge"></span>
        </div>
      </div>
      <div v-else-if="game_instances.length <= 0" class="text-center py-3">
        <span style="font-size: 2em"><i class="tim-icons icon-calendar-60"></i></span>
        <h4 class="mt-2 text-white" style="font-size: 14px">No bookings on this day</h4>
      </div>
      <div v-else class="tp-picker-list">
        <div v-for="item in sortedDrawerBookings"
             :key="'tpp' + item.booking_slot_id"
             :class="pickerRowClass(item)"
             @click="toggleRowSelected(item)">
          <div class="tp-picker-row__time">
            <span v-if="!isPublicChild(item)">{{getTime(item.booking_time)}}</span>
          </div>
          <div class="tp-picker-event">
            <div class="tp-picker-event__thumb-wrap" :class="{'is-selected': item.selected}">
              <img v-if="item.game_img" v-twin-src="item.game_img" class="tp-picker-event__img" alt="" />
              <div v-else class="tp-picker-event__img tp-picker-event__img--placeholder"></div>
              <div class="tp-picker-event__check" :class="{'is-checked': item.selected}">
                <i class="tim-icons icon-check-2"></i>
              </div>
            </div>
            <div class="tp-picker-event__text">
              <div class="text-white tp-row-title">{{item.name}}</div>
              <small v-if="isPublicChild(item)" class="text-white tp-row-sub">
                {{item.purchaser_first_name}} {{item.purchaser_last_name}}<span v-if="item.order_number"> · #{{item.order_number}}</span><span v-if="item.group_size"> · {{item.group_size}} guests</span>
              </small>
              <small v-else-if="isPublicMaster(item)" class="text-white tp-row-sub">All parties on public slot · {{item.child_count}} parties</small>
            </div>
            <div v-if="!isPublicChild(item)" class="tp-picker-event__status">
              <badge v-if="item.status === 'available' || item.status === 'completed'" type="success">{{item.status}}</badge>
              <badge v-if="item.status === 'booked'" type="info">{{item.status}}</badge>
              <badge v-if="item.status === 'expired'" type="default">passed</badge>
              <badge v-if="item.status === 'cancelled' || item.status === 'blocked'" type="danger">{{item.status}}</badge>
              <badge v-if="item.status === 'call_to_book'" type="warning">call to book</badge>
            </div>
          </div>
        </div>
      </div>
      <div v-if="!drawerLoading && drawerMode === 'party' && (drawerShowAllStatuses || hiddenDrawerBookingsCount > 0)" class="tp-picker-show-all">
        <button type="button" class="tp-picker-show-all__btn" @click="drawerShowAllStatuses = !drawerShowAllStatuses">
          <span v-if="!drawerShowAllStatuses">Show {{hiddenDrawerBookingsCount}} unbooked slots</span>
          <span v-else>Hide unbooked slots</span>
        </button>
      </div>
    </drawer-section>

    <!-- Event mode (was select_game_instance modal) -->
    <drawer-section v-if="drawerMode === 'event'" title="Events" icon="icon-shape-star">
      <div v-if="drawerLoading">
        <div v-for="n in 3" :key="'ple' + n" class="d-flex align-items-center mb-2" style="gap: 12px">
          <span class="skel skel--dot"></span>
          <div style="flex: 1">
            <div class="skel" style="width: 60%; height: 14px; margin-bottom: 6px"></div>
            <div class="skel skel--line-sm"></div>
          </div>
          <span class="skel skel--badge"></span>
        </div>
      </div>
      <div v-else-if="game_instances.length <= 0" class="text-center py-3">
        <span style="font-size: 2em"><i class="tim-icons icon-puzzle-10"></i></span>
        <h4 class="mt-2 text-white" style="font-size: 14px">No finished events yet for this day. End the event from the game tracker, then assign.</h4>
      </div>
      <div v-else class="tp-picker-list">
        <div v-for="item in sortedDrawerBookings"
             :key="'tpe' + item.booking_slot_id"
             :class="pickerRowClass(item)"
             @click="toggleRowSelected(item)">
          <div class="tp-picker-row__time">
            <span v-if="!isPublicChild(item)">{{getTime(item.booking_time)}}</span>
          </div>
          <div class="tp-picker-event">
            <div class="tp-picker-event__thumb-wrap" :class="{'is-selected': item.selected}">
              <img v-if="item.game_img" v-twin-src="item.game_img" class="tp-picker-event__img" alt="" />
              <div v-else class="tp-picker-event__img tp-picker-event__img--placeholder"></div>
              <div class="tp-picker-event__check" :class="{'is-checked': item.selected}">
                <i class="tim-icons icon-check-2"></i>
              </div>
            </div>
            <div class="tp-picker-event__text">
              <div class="text-white tp-row-title">{{item.name}}</div>
              <small v-if="isPublicChild(item)" class="text-white tp-row-sub">
                {{item.purchaser_first_name}} {{item.purchaser_last_name}}<span v-if="item.order_number"> · #{{item.order_number}}</span><span v-if="item.group_size"> · {{item.group_size}} guests</span><span v-if="item.first_name || item.last_name"> · Host: {{item.first_name}} {{item.last_name}}</span>
              </small>
              <small v-else-if="item.first_name || item.last_name" class="text-white tp-row-sub">Host: {{item.first_name}} {{item.last_name}}</small>
            </div>
          </div>
        </div>
      </div>
    </drawer-section>

    <!-- Players mode (was select_players modal) -->
    <drawer-section v-if="drawerMode === 'players'" title="Add participants" icon="icon-badge">
      <segmented-toggle
        class="mb-3"
        size="sm"
        :value="activeTabIndex"
        :options="participantSubmodeOptions"
        @input="switchTab"
      />

      <!-- Existing customer lookup -->
      <div v-if="activeTabIndex === 0">
        <base-input label="Customer lookup (search by name, email, or phone)">
          <el-autocomplete style="width: 100%"
                           v-model="lookup_value"
                           :fetch-suggestions="querySearch"
                           placeholder="Search"
                           :trigger-on-focus="false"
                           @select="handleSelect">
            <template #default="{ item }">
              <p style="margin-bottom: -12px" class="text-primary">{{ item.first_name }} {{ item.last_name }}</p>
              <small class="font-italic">
                <i class="tim-icons icon-email-85"></i> {{item.email }} &nbsp;
                <i v-if="item.phone" class="tim-icons el-icon-mobile-phone"></i> {{item.phone }}
              </small>
            </template>
          </el-autocomplete>
        </base-input>
        <base-button size="sm" type="primary" v-on:click="addCustomer()">Add</base-button>
      </div>

      <!-- New customer form -->
      <div v-if="activeTabIndex === 1">
        <div class="row">
          <div class="col-6">
            <base-input :error="errs.first_name" label="First name" v-model="user.first_name" placeholder="First name"></base-input>
          </div>
          <div class="col-6">
            <base-input :error="errs.last_name" label="Last name" v-model="user.last_name" placeholder="Last name"></base-input>
          </div>
        </div>
        <div class="row">
          <div class="col-6">
            <base-input :error="errs.email" label="Email" v-model="user.email" placeholder="Email"></base-input>
          </div>
          <div class="col-6">
            <base-input type="text" label="Phone (Optional)">
              <phone-number-input ref="phoneInput"
                                  v-model="user.phone" @phone-update="handlePhoneUpdate"
                                  :default-country="countryCode || 'US'" />
            </base-input>
          </div>
        </div>
        <base-button size="sm" type="primary" v-on:click="addCustomer()">Add customer</base-button>
      </div>

      <!-- Added participants list -->
      <div v-if="end_users.length > 0" class="tp-picker-list mt-3">
        <div v-for="(eu, i) in end_users" :key="'eu' + i" class="tp-picker-row">
          <div class="tp-picker-event">
            <div class="tp-picker-event__thumb-wrap">
              <div class="tp-participant-avatar">{{participantInitials(eu)}}</div>
            </div>
            <div class="tp-picker-event__text">
              <div class="text-white tp-row-title">{{eu.first_name}} {{eu.last_name}}</div>
              <small class="text-white tp-row-sub">{{eu.email}}</small>
            </div>
            <button type="button" class="tp-participant-remove" @click.stop="removeCustomer(i)" aria-label="Remove">
              <i class="tim-icons icon-simple-remove"></i>
            </button>
          </div>
        </div>
      </div>
      <div v-else class="text-center py-3 mt-3">
        <span style="font-size: 2em"><i class="tim-icons icon-badge"></i></span>
        <h4 class="mt-2 text-white" style="font-size: 14px">No participants added yet</h4>
      </div>
    </drawer-section>

    <template #footer>
      <div class="footer-actions">
        <base-button size="sm" type="secondary" @click="closeAssignmentDrawer">Cancel</base-button>
        <base-button v-if="drawerMode !== 'players' || end_users.length > 0"
                     size="sm"
                     type="primary"
                     @click="assignPhoto()">
          Assign photo
        </base-button>
      </div>
    </template>
  </base-drawer>

      <base-alert-dialog :show="sizeDialog.show"
                         title="Photos too large to email"
                         variant="warning"
                         show-cancel
                         confirm-text="Send with links"
                         cancel-text="Choose fewer photos"
                         @confirm="sizeDialog.show = false; emailToGroup({ allowLinks: true })"
                         @cancel="sizeDialog.show = false">
        The selected photos add up to {{sizeDialog.totalMb}} MB as an email. Most inboxes refuse emails over {{sizeDialog.limitMb}} MB, so this one would never arrive.
        <br><br>
        <strong class="text-white">Send with links</strong> attaches as many photos as fit and adds download links for the rest, all at full quality.
        <br><br>
        <strong class="text-white">Choose fewer photos</strong> goes back so you can uncheck some and send them in a second email.
      </base-alert-dialog>
      <base-alert-dialog :show="alertDialog.show"
                         :title="alertDialog.title"
                         :message="alertDialog.message"
                         :variant="alertDialog.variant"
                         :show-cancel="alertDialog.showCancel"
                         :confirm-text="alertDialog.confirmText"
                         :cancel-text="alertDialog.cancelText"
                         :confirm-variant="alertDialog.confirmVariant"
                         @confirm="resolveAlert(true)"
                         @cancel="resolveAlert(false)" />
      <adjust-overlay-drawer
        :show="adjust.show"
        :photo-url="adjust.photo ? adjust.photo.url : ''"
        :overlay="adjust.photo ? adjust.photo.selectedOverlay : null"
        :override="adjust.photo ? adjust.photo.overlay_layers : null"
        :resolve-text="content => adjust.photo ? processDynamicContent(content, adjust.photo) : content"
        @close="closeAdjust"
        @apply="onAdjustApply" />
      <adjust-overlay-drawer
        :show="!!pendingPhoto"
        pending
        :photos="pendingPhoto ? [{ uploaded_image: pendingPhoto.url, overlay_id: pendingPhoto.overlayId || null, overlay_layers: null }] : null"
        :photo-index="0"
        :overlays="overlays"
        :resolve-text="content => content"
        @close="cancelPending"
        @retake="retakePending"
        @use="usePending" />
      <!-- Live overlay camera (Photo settings > Camera). Dynamic variables cannot resolve before assignment. -->
      <live-camera-capture
        :show="liveCamera.show"
        :overlays="overlays"
        :overlay-id="liveCamera.overlayId"
        :resolve-text="content => content"
        @close="closeLiveCamera"
        @use-native="useNativeCamera"
        @capture="onLiveCapture" />
      <input ref="nativeInput" type="file" :accept="isAndroid14 ? undefined : 'image/*'" style="display: none" @change="takePhoto()">
  </div>
</template>

<script>
import {Auth} from "aws-amplify";
import {API_LOCATION} from "@/api-config";
import {formattedDate, time, dayOfWeek} from "@/plugins/dateFormatter";
import {formatCustomTime} from "@/util/customTime";
import axios from "axios";
import {Dropdown, Autocomplete, DropdownMenu} from 'element-ui'
import Badge from "@/components/Badge";
import _ from "lodash";
import { bakeOverlay, preparePhotoCanvas } from '@/util/overlayRenderer';
import { effectiveLayers, cropsToOverlay } from '@/util/overlayGeometry';
import AdjustOverlayDrawer from '@/components/Photos/AdjustOverlayDrawer.vue';
import LiveCameraCapture from '@/components/Photos/LiveCameraCapture.vue';
import { PART_BUDGET_BYTES, photoFileName, startsNewPart, estimatePartCount, partFileName, buildZipBlob } from '@/util/photoZip';
import PhoneNumberInput from '@/components/PhoneNumberInput.vue';

// JPEG quality for canvas exports (overlay bake and download). Quality 1
// disables quantization and produced files 3 to 4 times larger than the phone
// original (a 5 MB photo became an 18 MB composite), which then failed to
// email. 0.95 is the top of the professional band (above what the phone itself
// captured at) and keeps the file near the source size.
const OVERLAY_EXPORT_QUALITY = 0.95

const NO_RECIPIENTS_MESSAGE = 'Photos are emailed to the players on this booking who signed a waiver with an email address, and none of them has one. Add an email to a player, or assign the photo to a customer on the Photos page and send it from there.'
// True when the photo email endpoint rejected the send because nobody on the
// booking has an email address (400 with error 'No valid recipients').
function isNoRecipientsError(err) {
  if (!err || !err.response || err.response.status !== 400) return false
  let data = err.response.data
  if (typeof data === 'string') { try { data = JSON.parse(data) } catch (e) { return false } }
  return !!(data && data.error === 'No valid recipients')
}

export default {
  name: "TakePhotos",
  components: {
    AdjustOverlayDrawer,
    LiveCameraCapture,
    [Dropdown.name]: Dropdown,
    Badge,
    [Autocomplete.name]: Autocomplete,
    [DropdownMenu.name]: DropdownMenu,
    PhoneNumberInput
  },
  data() {
    return {
      // Adjust overlay drawer: which photo is being adjusted and its grid index.
      adjust: { show: false, photo: null, index: -1 },
      // Review before saving (Photo settings toggle): the picked file waits here until Use photo.
      review_before_save: false,
      pendingPhoto: null,
      // Camera choice (Photo settings): 'native' opens the device camera, 'live' the in page camera view.
      camera_mode: 'native',
      liveCamera: { show: false, overlayId: null },
      countryCode: JSON.parse(localStorage.getItem('company')).country,
      feature_not_enabled: false,
      errs: {
        first_name: '',
        last_name: '',
        email: ''
      },
      add_another_player: false,
      lookup_value: '',
      user: {
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        end_user_id: '',
        phoneE164: ''
      },
      default_aspect_ratio: '',
      confirm_before_delete: false,
      overlays: [],
      loading: false,
      date_format: JSON.parse(localStorage.getItem('group')).date_format,
      time_format: JSON.parse(localStorage.getItem('group')).time_format,
      end_users: [],
      email_active: 1,
      game_instances: '',
      uploadUrl: '',
      file: '',
      photos: '',
      modals: {
        email_group: false,
        email_customer_photos: false,
        edit_team: false
      },
      selectedPhoto: '',
      emailPhotoCandidates: [],
      sizeDialog: { show: false, totalMb: 0, limitMb: 18 },
      zipState: { running: false, done: 0, total: 0 },
      photosLoading: true,
      skeletonsVisible: false,
      skeletonTimer: null,
      alertDialog: { show: false, title: '', message: '', variant: 'warning', showCancel: false, confirmText: 'OK', cancelText: 'Cancel', confirmVariant: 'primary' },
      alertResolve: null,
      emailGroupPurchaserEmail: null,
      emailGroupPurchaser: null,
      emailCustomerTarget: null,
      sendingEmail: false,
      emailUsersLoading: false,
      selectedTeam: '',
      current: '',
      current_booking: '',
      activeTabIndex: 0,
      drawerOpen: false,
      drawerMode: 'party',
      drawerLoading: false,
      drawerPhoto: null,
      drawerShowAllStatuses: false,
      drawerModeOptions: [
        { value: 'party', label: 'Booking party' },
        { value: 'event', label: 'Completed event' },
        { value: 'players', label: 'Participants' }
      ],
      participantSubmodeOptions: [
        { value: 0, label: 'Existing customer' },
        { value: 1, label: 'New customer' }
      ],
    }
  },
  computed: {
    canDownloadAll(){
      return !this.zipState.running && !this.photosLoading && Array.isArray(this.photos) && this.photos.length > 0
    },
    // Names the action and, once every card has measured its bytes, the size.
    downloadAllTooltip(){
      if(this.zipState.running) return `Zipping ${this.zipState.done} of ${this.zipState.total}`
      if(this.photosLoading) return 'Loading photos'
      if(!Array.isArray(this.photos) || this.photos.length === 0) return 'No photos on this day to download'
      const count = this.photos.length
      const noun = count === 1 ? 'photo' : 'photos'
      const allSized = this.photos.every(p => p.size_bytes)
      if(allSized){
        const mb = Math.round(this.photos.reduce((sum, p) => sum + p.size_bytes, 0) / (1024 * 1024))
        return `Download ${count} ${noun}, about ${mb} MB`
      }
      return `Download ${count} ${noun} on this day`
    },
    // Mirrors the backend budget math for display only; the lambda's 413 gate
    // stays authoritative. 18 MB budget, base64 4/3 inflation, 100 KB overhead.
    selectedEmailMb(){
      const selected = this.emailPhotoCandidates.filter(c => c.selected && c.size_bytes)
      if(selected.length === 0) return null
      const encoded = 100 * 1024 + selected.reduce((sum, c) => sum + Math.ceil(c.size_bytes / 3) * 4, 0)
      return Number((encoded / (1024 * 1024)).toFixed(1))
    },
    selectedEmailOverBudget(){
      return this.selectedEmailMb !== null && this.selectedEmailMb > 18
    },
    isAndroid14() {
      return navigator.userAgent.match(/Android/i) !== null;
    },
    liveCameraSupported() {
      return typeof navigator !== 'undefined' && !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia) && window.isSecureContext
    },
    useLiveCamera() {
      return this.camera_mode === 'live' && this.liveCameraSupported
    },
    hiddenDrawerBookingsCount(){
      if(this.drawerMode !== 'party') return 0
      if(!Array.isArray(this.game_instances)) return 0
      const allowed = new Set(['booked', 'completed', 'cancelled'])
      return this.game_instances.filter(r => !allowed.has(r.status)).length
    },
    emailGroupTitle(){
      return this.emailPhotoCandidates.length > 1 ? 'Email photos' : 'Email photo'
    },
    sortedDrawerBookings() {
      if(!Array.isArray(this.game_instances)) return []

      // Status filter applies to Party mode only. Event mode game_instances don't carry bs.status.
      let rows = this.game_instances
      if(this.drawerMode === 'party' && !this.drawerShowAllStatuses){
        const allowed = new Set(['booked', 'completed', 'cancelled'])
        rows = rows.filter(r => allowed.has(r.status))
      }

      // Sort everything by booking_time ascending first
      const timeSorted = [...rows].sort((a, b) => {
        const ta = a.booking_time || ''
        const tb = b.booking_time || ''
        return ta < tb ? -1 : ta > tb ? 1 : 0
      })

      // Bucket children by their master's booking_slot_id, keep non-children in order
      const childrenByMaster = new Map()
      const nonChildren = []
      for(const row of timeSorted){
        if(row.master_slot_id != null){
          const kids = childrenByMaster.get(row.master_slot_id) || []
          kids.push(row)
          childrenByMaster.set(row.master_slot_id, kids)
        } else {
          nonChildren.push(row)
        }
      }

      // Walk non-children; inject each master's children immediately after it
      const result = []
      const masterIdsSeen = new Set()
      for(const row of nonChildren){
        result.push(row)
        const kids = childrenByMaster.get(row.booking_slot_id)
        if(kids){
          result.push(...kids)
          masterIdsSeen.add(row.booking_slot_id)
        }
      }

      // Orphan children (master not present in dataset) — append at end
      for(const [masterId, kids] of childrenByMaster.entries()){
        if(!masterIdsSeen.has(masterId)) result.push(...kids)
      }

      return result
    },
    photoCountByBooking() {
      const counts = {}
      if (!this.photos || !this.photos.length) return counts
      for (const photo of this.photos) {
        if (!photo.assignees) continue
        for (const assignee of photo.assignees) {
          if (assignee.booking_slot_id) {
            counts[assignee.booking_slot_id] = (counts[assignee.booking_slot_id] || 0) + 1
          }
        }
      }
      return counts
    },
    photoCountByUser() {
      const counts = {}
      if (!this.photos || !this.photos.length) return counts
      for (const photo of this.photos) {
        if (!photo.assignees) continue
        for (const assignee of photo.assignees) {
          if (assignee.user_id) {
            counts[assignee.user_id] = (counts[assignee.user_id] || 0) + 1
          }
        }
      }
      return counts
    },
  },
  methods: {
    handlePhoneUpdate(phoneData) {
      this.user.phoneE164 = phoneData.e164;
    },
    async saveTeamName(){
      const token = await this.getAuthToken()
      const config = {headers: {Authorization: token}}
      const companyId = localStorage.getItem('companyId')
      const url = API_LOCATION + 'companies/' + companyId + '/teams'

      const newTeamName = this.selectedTeam.team_name
      const bookingSlotId = this.selectedTeam.booking_slot_id

      const data = {
        team_name: newTeamName,
        booking_slot_id: bookingSlotId
      }
      this.loading = true

      try {
        await axios.put(url, data, config)
      } catch (err) {
        console.log(err)
        this.loading = false
        const status = err.response && err.response.status
        if (status == 401) {
          this.showAlert({
            title: 'Error',
            message: 'You do not have access to this feature. Reach out to your admin regarding your site permissions.',
            variant: 'danger'
          }).then((response) => {
            this.$router.push('/')
          })
        } else if (status == 500) {
          this.showAlert({
            title: 'Error',
            message: err.response.data,
            variant: 'danger'
          }).then((response) => {
            this.$router.push('/')
          })
        } else {
          this.showAlert({ title: 'Error', message: 'Something went wrong', variant: 'danger' })
        }
        return
      }

      this.modals.edit_team = false
      this.$notify({ type: 'success', message: 'Team has been updated', icon: 'tim-icons icon-check-2' })
      this.loading = false

      // Patch in-memory assignees so re-bake uses the new team name without
      // waiting on a getPhotos round-trip. Photos other than the one the modal
      // was opened on may also be assigned to this booking.
      for (const photo of this.photos) {
        if (!photo.assignees) continue
        for (const assignee of photo.assignees) {
          if (assignee.booking_slot_id === bookingSlotId) {
            this.$set(assignee, 'team_name', newTeamName)
          }
        }
      }

      await this.rebakeOverlaysForBookingSlot(bookingSlotId)

      // Background refresh to pick up any concurrent changes from other staff.
      this.getPhotos()
    },
    async rebakeOverlaysForBookingSlot(bookingSlotId){
      await this.$nextTick()
      for (let i = 0; i < this.photos.length; i++) {
        const photo = this.photos[i]
        if (!photo.overlay_id || !photo.selectedOverlay) continue

        const assignedToBooking = (photo.assignees || []).some(
          a => a.booking_slot_id === bookingSlotId
        )
        if (!assignedToBooking) continue

        let hasTeamNameLayer = false
        try {
          const layers = effectiveLayers(photo.selectedOverlay, photo.overlay_layers).layers
          hasTeamNameLayer = layers.some(
            l => l.type === 'text' && typeof l.content === 'string' && l.content.includes('[Team Name]')
          )
        } catch (err) {
          console.error('Failed to parse overlay layers for re-bake:', err)
        }
        if (!hasTeamNameLayer) continue

        // Force saveOverlay to composite from the original photo, not the
        // stale baked PNG that has the old (empty) team name burned in.
        photo.overlay_url = ''
        try {
          await this.saveOverlay(photo, i)
        } catch (err) {
          console.error('Failed to re-bake overlay for photo', photo.id, err)
        }
      }
    },
    editTeam(item){
      this.selectedTeam = item
      this.modals.edit_team = true
    },
    viewGameDetails(item){
      let route = this.$router.resolve({ path: '/games/history/instance', query: { id: item.game_instance_id }})
      window.open(route.href, '_blank')
    },
    goToBilling(){
      this.$router.push('/settings/billing')
    },
    hasPermissions(scope){
      let permissions = JSON.parse(localStorage.getItem('permissions'))
      const matchFound = permissions.some(element => element === scope);
      return matchFound
    },
    isPublicChild(item){
      return item && item.master_slot_id != null
    },
    isPublicMaster(item){
      return item && item.master_slot_id == null && Number(item.child_count || 0) > 0
    },
    pickerRowClass(item){
      if(!item) return ''
      const parts = ['tp-picker-row']
      if(this.isPublicChild(item)) parts.push('tp-picker-row--child')
      else if(this.isPublicMaster(item)) parts.push('tp-picker-row--master')
      if(item.selected) parts.push('tp-picker-row--selected')
      return parts.join(' ')
    },
    toggleRowSelected(item){
      if(!item) return
      this.$set(item, 'selected', !item.selected)
    },
    toggleRecipient(eu){
      if(!eu) return
      this.$set(eu, 'selected', !eu.selected)
    },
    participantInitials(eu){
      if(!eu) return '·'
      const first = (eu.first_name || '').charAt(0)
      const last = (eu.last_name || '').charAt(0)
      return (first + last).toUpperCase() || '·'
    },
    openAssignmentDrawer(p){
      this.selectedPhoto = p
      this.drawerPhoto = p
      this.end_users = []
      this.game_instances = []
      this.add_another_player = false
      this.clearCustomer()
      this.setBookingDateFromPhoto(p)
      this.drawerMode = 'party'
      this.drawerShowAllStatuses = false
      this.drawerOpen = true
      this.getGameInstances(8)
    },
    closeAssignmentDrawer(){
      this.drawerOpen = false
      this.drawerPhoto = null
      this.game_instances = []
      this.end_users = []
      this.add_another_player = false
      this.drawerShowAllStatuses = false
      this.clearCustomer()
    },
    onDrawerModeChange(mode){
      this.drawerMode = mode
      this.game_instances = []
      this.end_users = []
      this.drawerShowAllStatuses = false
      if(mode === 'event'){
        this.getGameInstances(7)
      } else if(mode === 'party'){
        this.getGameInstances(8)
      }
    },
    onDrawerDateChange(){
      if(this.drawerMode === 'event'){
        this.getGameInstances(7)
      } else if(this.drawerMode === 'party'){
        this.getGameInstances(8)
      }
    },
    setBookingDateFromPhoto(p){
      if(p && p.timestamp){
        let d = new Date(p.timestamp)
        if(!isNaN(d.getTime())){
          this.current_booking = this.formatDateString(d)
          return
        }
      }
      this.setToday()
    },
    switchTab(index){
      this.activeTabIndex = index
    },
    removeCustomer(index){
      this.end_users.splice(index, 1);
    },
    cancelAddAnotherPlayer(){
      this.add_another_player = false
      this.clearCustomer()
    },
    validateCustomer(){
      let errors = 0

      if(this.user.first_name === ''){
        this.errs.first_name = "First name cannot be empty"
        errors++
      } else {
        this.errs.first_name = ''
      }

      if(this.user.last_name === ''){
        this.errs.last_name = "Last name cannot be empty"
        errors++
      } else {
        this.errs.last_name = ''
      }

      if(this.user.email === ''){
        this.errs.email = "Email cannot be empty"
        errors++
      } else {
        this.errs.email = ''
      }
      return errors > 0 ? false : true
    },
    addCustomer(){
      if(!this.validateCustomer()){
        return
      }
      this.user.phone = this.user.phoneE164
      this.end_users.push(this.user)
      this.clearCustomer()
    },
    clearCustomer(){
      this.lookup_value = ''
      this.user = {
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        end_user_id: '',
        phoneE164: ''
      }
    },
    async querySearch(queryString, cb) {
      //make API call to /customers with queryString as filter
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId +
        '/customers?count=10000000&offset=0&first_name=&last_name=&email=&phone=&game=&tag=&autocomplete=' + encodeURIComponent(queryString)
      axios.get(url, config)
        .then(response => {
          let suggestions = []
          for(let n in response.data.customers){
            if(response.data.customers[n].email != null
              && response.data.customers[n].first_name != null
              && response.data.customers[n].first_name != ''){
              suggestions.push({
                value: JSON.stringify(response.data.customers[n]),
                email: response.data.customers[n].email,
                id: response.data.customers[n].id,
                end_user_id: response.data.customers[n].end_user_id,
                first_name: response.data.customers[n].first_name,
                last_name: response.data.customers[n].last_name,
                phone: response.data.customers[n].phone
              })
            }

          }
          const results = suggestions;

          cb(results)
        })
        .catch(err => {
          this.loading = false
          console.log(err)
          this.handleApiError(err)
        })
    },
    handleSelect(item){
      this.lookup_value = item.email
      this.user.first_name = item.first_name
      this.user.last_name = item.last_name
      this.user.email = item.email
      this.user.phone = item.phone
      this.user.end_user_id = item.end_user_id
    },
    emailButtonLabel(item){
      if(!item){
        return
      }
      if(!this.email_active){
        return 'You must activate the photo email template'
      }
      if(item.hasOwnProperty('user_id')){
        const count = this.photoCountByUser[item.user_id] || 1
        return count > 1 ? `Email to customer (${count} photos)` : 'Email to customer'
      }
      const count = this.photoCountByBooking[item.booking_slot_id] || 1
      return count > 1 ? `Email to group (${count} photos)` : 'Email to group'
    },
    getDayOfWeek(d){
      if(JSON.parse(localStorage.getItem('group')).show_day_of_week){
        return dayOfWeek(d) + ', '
      }
      return ''
    },
    async addCustomerWaiver(){
      this.loading = true
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let groupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + groupId + '/bookings/' + this.selectedPhoto.booking_slot_id + '/waivers'

      let data = {
        user: {
          ...this.user,
          phone: this.user.phoneE164 || this.user.phone  // Use E.164 if available
        }
      }
      axios.post(url, data, config)
        .then(response => {
          // The add creates a waiver on the booking, so label and pre-check the
          // row exactly as it will appear on the next open. Push a copy: the
          // lookup form reuses this.user for the next entry.
          this.end_users.push({ ...this.user, from_waiver: true, selected: 1 })
          this.loading = false
        }).catch((err) => {
        console.log(err)
        this.loading = false
        this.handleApiError(err)
      })

    },
    async removeAssignment(photo_id, link_id, link_type){
      this.loading = true
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let groupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + groupId + '/photos/' + photo_id + '/assign?link_id=' + link_id + '&link_type=' + link_type

      axios.delete(url, config)
        .then(response => {
          this.loading = false
          this.$notify({ type: 'success', message: 'Photo has been unassigned', icon: 'tim-icons icon-check-2' })
          this.getPhotos()
        }).catch((err) => {
        console.log(err)
        this.loading = false
        this.handleApiError(err)
      })
    },
    async preloadImage(url) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error(`Failed to preload image: ${url}`));
        img.src = url;
      });
    },
    async clearOverlay(item, index){
      const canvas = document.getElementById('cv' + index);
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      item.selectedOverlay = ""
      item.overlay_id = null
      item.overlay_layers = null
      item.overlay_url = ""
      // Set individual loading state
      this.$set(item, 'isProcessing', true);
      var token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let groupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + groupId + '/photos/' + item.id + '/overlay'

      let data = {
        overlay_url:  "",
        overlay_id: null
      }

      try {
        const response = await axios.post(url, data, config);

        // Preload the image without overlay before updating display
        const newImageUrl = item.url + '?t=' + Date.now();
        await this.preloadImage(newImageUrl);

        // Now update the display
        const img = document.getElementById('img' + index);
        if (img) {
          this.$set(item, 'isSizing', true)
          img.src = newImageUrl;
        }

        this.$set(item, 'isProcessing', false); // Turn off individual loading
        this.file = ''
        this.uploadUrl = ''
      } catch (err) {
        console.log(err)
        this.$set(item, 'isProcessing', false); // Turn off loading on error
        this.showAlert({
          title: 'Error',
          message: 'You do not have access to this feature. Reach out to your admin regarding your site permissions.',
          variant: 'danger'
        }).then((response)=> {
          this.$router.push('/')
        })
      }
    },
    loadImage(url) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error(`Failed to load image at ${url}`));
        img.src = url;
      });
    },
    parseAspectRatio(ratioString) {
      // Parse "16:9" -> 16/9 = 1.78
      if (!ratioString) return null
      const parts = ratioString.split(':')
      if (parts.length !== 2) return null
      return parseFloat(parts[0]) / parseFloat(parts[1])
    },
    async findMatchingOverlay(gameId, completed, photoUrl) {
      // Find overlays that have applied_by_default = true and match this game
      const matchingOverlays = this.overlays.filter(overlay => {
        if (!overlay.applied_by_default) return false

        // Parse game_defaults if it's a string
        let gameDefaults = overlay.game_defaults
        if (typeof gameDefaults === 'string') {
          gameDefaults = JSON.parse(gameDefaults)
        }
        if (!gameDefaults || gameDefaults.length === 0) return false

        // Check if this game is in the defaults
        const gameDefault = gameDefaults.find(gd => gd.game_id === gameId)
        if (!gameDefault) return false

        // Check apply_condition
        // Note: completed may be 1/0 from MySQL instead of true/false
        const condition = gameDefault.apply_condition
        const isCompleted = completed === true || completed === 1
        const isNotCompleted = completed === false || completed === 0

        if (condition === 'always') return true
        if (condition === 'escaped' && isCompleted) return true
        if (condition === 'not_escaped' && isNotCompleted) return true
        // If completed is null/undefined (no game tracker), only 'always' matches
        if (completed === null || completed === undefined) {
          return condition === 'always'
        }

        return false
      })

      if (matchingOverlays.length === 0) return null
      if (matchingOverlays.length === 1) return matchingOverlays[0]

      // Multiple matches - prefer the one with closest aspect ratio to the photo
      try {
        const img = await this.loadImage(photoUrl)
        const photoRatio = img.naturalWidth / img.naturalHeight

        let bestMatch = matchingOverlays[0]
        let smallestDiff = Infinity

        for (const overlay of matchingOverlays) {
          const overlayRatio = this.parseAspectRatio(overlay.aspect_ratio)
          if (overlayRatio !== null) {
            const diff = Math.abs(photoRatio - overlayRatio)
            if (diff < smallestDiff) {
              smallestDiff = diff
              bestMatch = overlay
            }
          }
        }

        return bestMatch
      } catch (err) {
        // If we can't load the image, just return first match
        console.error('Could not load image for aspect ratio detection:', err)
        return matchingOverlays[0]
      }
    },
    async autoApplyOverlay(photo, gameId, completed) {
      const matchingOverlay = await this.findMatchingOverlay(gameId, completed, photo.url)
      if (!matchingOverlay) return

      // Don't auto-apply if photo already has an overlay
      if (photo.overlay_id) return

      // Set the selected overlay and trigger save
      this.$set(photo, 'selectedOverlay', matchingOverlay)

      // Find the index of this photo
      const index = this.photos.findIndex(p => p.id === photo.id)
      if (index !== -1) {
        await this.saveOverlay(photo, index)
        // Update overlay_id so dropdown shows correctly
        this.$set(photo, 'overlay_id', matchingOverlay.id)
      }
    },
    // Adjust overlay drawer ----------------------------------------------
    openAdjust(item, index) {
      this.adjust = { show: true, photo: item, index }
    },
    closeAdjust() {
      this.adjust.show = false
    },
    async onAdjustApply({ overlayLayers }) {
      const item = this.adjust.photo
      const index = this.adjust.index
      if (!item) return
      this.$set(item, 'overlay_layers', overlayLayers)
      // Bake from the original so the adjustment is drawn once, not on top of the old composite.
      item.overlay_url = ''
      await this.saveOverlay(item, index)
      this.closeAdjust()
    },
    // The select is the overlay state: None clears, anything else applies. A
    // different template starts from its own positions and an adjustment belongs
    // to one overlay, so a photo with adjustments confirms before they are lost.
    async onOverlayPicked(item, index) {
      if (item.overlay_layers) {
        const ok = await this.showAlert({
          title: 'Discard adjustments?',
          message: 'This photo has adjusted layers. Switching overlays starts from the new overlay as designed.',
          variant: 'warning',
          showCancel: true,
          confirmText: 'Switch',
          confirmVariant: 'danger',
        })
        if (ok !== true) {
          // Put the select back to what is actually on the photo.
          item.selectedOverlay = item.overlay_id ? (_.find(this.overlays, { id: item.overlay_id }) || '') : ''
          return
        }
      }
      this.$set(item, 'overlay_layers', null)
      this.saveOverlay(item, index)
    },
    async onClearPicked(item, index) {
      if (item.overlay_layers) {
        const ok = await this.showAlert({
          title: 'Discard adjustments?',
          message: 'This photo has adjusted layers. Clearing the overlay removes them.',
          variant: 'warning',
          showCancel: true,
          confirmText: 'Clear',
          confirmVariant: 'danger',
        })
        if (ok !== true) return
      }
      await this.clearOverlay(item, index)
    },
    async saveOverlay(item, index) {
      // Set individual loading state
      this.$set(item, 'isProcessing', true);

      try {
        // Always bake from the untouched original. Baking on top of a previous
        // composite stacked overlays and added a JPEG generation on every re-apply.
        const baseImage = await this.loadImage(item.url);
        // An overlay with crop on keeps only the centered area with its aspect ratio.
        const { canvas: compositeCanvas, ctx, width: photoWidth, height: photoHeight } = preparePhotoCanvas(baseImage, {
          aspectRatio: item.selectedOverlay.aspect_ratio,
          crop: cropsToOverlay(item.selectedOverlay),
        });

        // Render the overlay through the shared renderer: the design canvas is
        // scaled by one uniform factor to fit the photo and centered, so an
        // aspect mismatch never stretches layers. Same code the editor previews with.
        // A photo with a stored adjustment bakes from it; otherwise from the template.
        const eff = effectiveLayers(item.selectedOverlay, item.overlay_layers)
        await bakeOverlay({
          ctx,
          layers: eff.layers,
          aspectRatio: item.selectedOverlay.aspect_ratio,
          referenceWidth: eff.referenceWidth,
          referenceHeight: eff.referenceHeight,
          photoWidth,
          photoHeight,
          resolveText: (content) => this.processDynamicContent(content, item),
        });

        // Convert canvas to blob and create file - wrapped in Promise for proper await
        await new Promise((resolve, reject) => {
          compositeCanvas.toBlob(async (blob) => {
            try {
              // Create file as high-quality JPEG
              this.file = new File([blob], 'composite_overlay.jpg', { type: 'image/jpeg' });

              // Rest of your upload logic...
              await this.saveFile();

              let token = await this.getAuthToken();
              let config = {headers: {Authorization: token}};
              let companyId = localStorage.getItem('companyId');
              let groupId = JSON.parse(localStorage.getItem('group')).id;
              let url = API_LOCATION + 'companies/' + companyId + '/groups/' + groupId + '/photos/' + item.id + '/overlay';

              item.overlay_url = this.uploadUrl.split("?")[0];
              item.overlay_id = item.selectedOverlay.id;

              let data = {
                overlay_url: this.uploadUrl.split("?")[0],
                overlay_id: item.selectedOverlay.id,
                overlay_layers: item.overlay_layers || null
              };

              const response = await axios.post(url, data, config);

              const newImageUrl = item.overlay_url + '?t=' + Date.now();
              await this.preloadImage(newImageUrl);

              const img = document.getElementById('img' + index);
              if (img) {
                this.$set(item, 'isSizing', true)
                img.src = newImageUrl;
              }

              this.$set(item, 'isProcessing', false);
              this.file = '';
              this.uploadUrl = '';
              resolve();

            } catch (err) {
              console.log(err);
              this.$set(item, 'isProcessing', false);
              this.handleUploadError(err);
              reject(err);
            }
          }, 'image/jpeg', OVERLAY_EXPORT_QUALITY);
        });

      } catch (error) {
        console.error('Error in saveOverlay:', error);
        this.$set(item, 'isProcessing', false); // Turn off loading on error
        this.showAlert({ title: 'Error', message: 'Failed to create overlay', variant: 'danger' });
      }
    },
    async processDynamicContent(content, item) {
      if (content.includes('[Team Name]')) {
        if (item.assignees.length > 0 && item.assignees[0].team_name) {
          content = item.assignees[0].team_name === null ?
            content.replaceAll('[Team Name]', '') :
            content.replaceAll('[Team Name]', item.assignees[0].team_name);
        } else {
          content = content.replaceAll('[Team Name]', '');
        }
      }

      if (content.includes('[Company Name]')) {
        content = item.company_name === null ?
          content.replaceAll('[Company Name]', '') :
          content.replaceAll('[Company Name]', item.company_name);
      }

      if (content.includes('[Location Name]')) {
        content = item.location_name === null ?
          content.replaceAll('[Location Name]', '') :
          content.replaceAll('[Location Name]', item.location_name);
      }

      if (content.includes('[Completion Time]')) {
        if (item.assignees.length > 0 && item.assignees[0].game_start_time && item.assignees[0].game_end_time) {
          let end_time = new Date(item.assignees[0].game_end_time);
          let start_time = new Date(item.assignees[0].game_start_time);
          let diffTime = (end_time - start_time);
          let total_minutes = parseInt(Math.floor(diffTime / (1000 * 60)));
          content = content.replaceAll('[Completion Time]', total_minutes + ' minutes');
        } else {
          content = content.replaceAll('[Completion Time]', '');
        }
      }

      if (content.includes('[Custom Time]')) {
        // formatCustomTime renders MM:SS (HH:MM:SS when needed) and blanks all-zero/empty
        content = content.replaceAll('[Custom Time]', formatCustomTime(item.assignees[0]?.custom_time));
      }

      if (content.includes('[Time Remaining]')) {
        if (item.assignees.length > 0 && item.assignees[0].game_start_time && item.assignees[0].game_end_time) {
          let end_time = new Date(item.assignees[0].game_end_time);
          let start_time = new Date(item.assignees[0].game_start_time);
          let diffTime = (end_time - start_time);
          let total_minutes = parseInt(Math.floor(diffTime / (1000 * 60)));
          let remaining = Number(item.assignees[0].game_duration) - total_minutes;
          if (remaining < 0) {
            remaining = 0;
          }
          content = content.replaceAll('[Time Remaining]', remaining + ' minutes');
        } else {
          content = content.replaceAll('[Time Remaining]', '');
        }
      }

      if (content.includes('[Number Of Hints]')) {
        if (item.assignees.length > 0) {
          content = item.assignees[0].number_of_hints === null ?
            content.replaceAll('[Number Of Hints]', '') :
            content.replaceAll('[Number Of Hints]', item.assignees[0].number_of_hints + ' hints');
        } else {
          content = content.replaceAll('[Number Of Hints]', '');
        }
      }

      if (content.includes('[Event Name]') || content.includes('[Game Name]')) {
        // [Game Name] is the legacy spelling of [Event Name]; overlays saved before the rename still carry it.
        let eventName = ''
        if (item.assignees.length > 0 && item.assignees[0].game_name !== null && item.assignees[0].game_name !== undefined) {
          eventName = item.assignees[0].game_name
        }
        content = content.replaceAll('[Event Name]', eventName).replaceAll('[Game Name]', eventName);
      }

      return content;
    },
    handleUploadError(err) {
      this.handleApiError(err)
    },
    formatDateString(d){
      let month = String(d.getMonth() + 1).padStart(2, '0')
      let day = String(d.getDate()).padStart(2, '0')
      return d.getFullYear() + '-' + month + '-' + day
    },
    changeDate(){
      // A date change is a new dataset: clear the grid and reload through the
      // same skeleton state as the initial load, instead of leaving the old
      // day's photos on screen under spinners until the response lands.
      this.photosLoading = true
      this.photos = []
      this.getPhotos()
    },
    getDate(d){
      return formattedDate(this.date_format, d)
    },
    getTime(d){
      return time(this.time_format, d)
    },
    getAuthToken() {
      return new Promise ( function(resolve) {
        Auth.currentSession()
          .then(data =>{
            resolve(data.idToken.jwtToken)
          })
          .catch(err => console.log(err))
      })
    },
    async getOverlays(){
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let groupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + groupId + '/photo-overlays'

      axios.get(url, config)
        .then(response => {
          this.overlays = response.data.overlays
          this.review_before_save = response.data.review_before_save === true
          this.camera_mode = response.data.camera_mode === 'live' ? 'live' : 'native'
          this.getPhotos()
        }).catch((err) => {
        console.log(err)
        this.photosLoading = false
        this.skeletonsVisible = false
        this.handleApiError(err)
      })
    },
    async getGameInstances (type) {
      this.drawerLoading = true
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/bookings?type=' + type + '&day=' + this.current_booking + '&include_children=1'

      this.axios.get(url, config)
        .then(response => {
          this.game_instances = response.data.bookings
          this.drawerLoading = false
        })
        .catch(err => {
          this.drawerLoading = false
          console.log(err)
          this.handleApiError(err)
        })
    },
    async saveFile() {
      this.uploadUrl = await this.getSignedURL(this.file)
      let uploaded = await this.uploadFile(this.file)
    },
    async getSignedURL (file) {
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let url = API_LOCATION + 'upload/photos'

      let payload = {
        filePath: file.name,
        contentType: file.type
      }

      return new Promise ( function(resolve, reject) {
        axios.post(url, payload, config)
          .then((res) => {
            resolve(res.data)
          })
          .catch((err) => {
            console.error(err)
            reject(err)
          })
      })
    },
    uploadFile (file) {
      let url = this.uploadUrl
      let config = { headers: {
          'Content-Type': file.type
        }}

      let payload = file
      return new Promise ( function(resolve, reject) {
        axios.put(url, payload, config)
          .then((res) => {
            resolve(res)
          })
          .catch((err) => {
            console.error(err)
            reject(err)
          })
      })
    },
    async assignPhoto(){
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId +
        '/photos/' + this.selectedPhoto.id + '/assign'

      // Collect selected game instances with their game_id, completed status, and all fields needed for overlay dynamic content
      let booking_slots = []
      let selectedGameData = []
      for(let n in this.game_instances){
        if(this.game_instances[n].selected){
          booking_slots.push(this.game_instances[n].booking_slot_id)
          selectedGameData.push({
            game_id: this.game_instances[n].game_id,
            completed: this.game_instances[n].completed,
            game_name: this.game_instances[n].name,
            custom_time: this.game_instances[n].custom_time,
            game_start_time: this.game_instances[n].game_start_time,
            game_end_time: this.game_instances[n].game_end_time,
            game_duration: this.game_instances[n].game_duration,
            number_of_hints: this.game_instances[n].number_of_hints,
            team_name: this.game_instances[n].team_name
          })
        }
      }

      let data = {
        users: this.end_users,
        booking_slots: booking_slots,
      }

      // Store reference to photo before clearing
      const photoId = this.selectedPhoto.id

      axios.post(url, data, config)
        .then(async response => {
          this.drawerOpen = false
          this.add_another_player = false
          this.clearCustomer()

          // Auto-apply overlay if conditions match
          if (selectedGameData.length > 0) {
            const photo = this.photos.find(p => p.id === photoId)
            if (photo && !photo.overlay_id) {
              const gameData = selectedGameData[0]
              // Temporarily set assignees with all fields needed for dynamic content
              this.$set(photo, 'assignees', [{
                game_name: gameData.game_name,
                custom_time: gameData.custom_time,
                game_start_time: gameData.game_start_time,
                game_end_time: gameData.game_end_time,
                game_duration: gameData.game_duration,
                number_of_hints: gameData.number_of_hints,
                team_name: gameData.team_name
              }])
              await this.autoApplyOverlay(photo, gameData.game_id, gameData.completed)
            }
          }

          this.game_instances = []
          this.end_users = []
          this.getPhotos()
        }).catch((err) => {
        this.loading = false
        if(err.response.status == 401){
          this.$router.push('/')
        } else {
          this.showAlert({
            title: 'Error',
            message: err.response.data,
            variant: 'danger',
            confirmText: 'Try again'
          })
        }
      })
    },
    async deletePhoto(p){
      const id = p.id
      if(this.confirm_before_delete){
        let result = await this.showAlert({
          title: 'Delete photo?',
          message: 'This action cannot be undone.',
          variant: 'warning',
          showCancel: true,
          confirmText: 'Delete',
          cancelText: 'Cancel'
        })
        if(result !== true) return
      }
      this.$set(p, 'deleting', true)
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/photos/' + id

      axios.delete(url, config)
        .then(response => {
          this.photos = this.photos.filter(photo => photo.id !== id);
          this.refreshPhotoSizing()
          this.$notify({ type: 'success', message: 'Photo has been deleted', icon: 'tim-icons icon-check-2' })
        }).catch((err) => {
        console.log(err)
        this.$set(p, 'deleting', false)
        this.handleApiError(err)
      })
    },
    setToday(){
      let current = new Date()
      this.current = this.formatDateString(current)
      this.current_booking = this.current
    },
    // showSkeletons is false where the per-photo loaders already cover the
    // wait, so the full screen mask doesn't double up on them.
    async getPhotos(showSkeletons = true){
      this.photosLoading = true
      // Two-timer rule: no indicator for fast responses, and skeletons only
      // when the grid has nothing better to show than ghosts.
      clearTimeout(this.skeletonTimer)
      if(showSkeletons){
        this.skeletonTimer = setTimeout(() => {
          if(this.photosLoading) this.skeletonsVisible = true
        }, 200)
      }
      const endPhotosLoading = () => {
        clearTimeout(this.skeletonTimer)
        this.photosLoading = false
        this.skeletonsVisible = false
      }
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let groupId = JSON.parse(localStorage.getItem('group')).id

      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + groupId + '/photos?t=' + this.current

      axios.get(url, config)
        .then(response => {
          this.photos = response.data.photos
          for(let n in this.photos){
            // Initialize individual loading state for each photo
            this.$set(this.photos[n], 'isProcessing', false)

            response.data.photos[n].selectedOverlay = _.find(this.overlays, {id: response.data.photos[n].overlay_id})
            if(JSON.parse(this.photos[n].bookings) && JSON.parse(this.photos[n].players)){
              this.photos[n].assignees = JSON.parse(this.photos[n].bookings).concat(JSON.parse(this.photos[n].players))
            } else if (JSON.parse(this.photos[n].bookings) && !JSON.parse(this.photos[n].players)){
              this.photos[n].assignees = JSON.parse(this.photos[n].bookings)
            } else if (!JSON.parse(this.photos[n].bookings) && JSON.parse(this.photos[n].players)){
              this.photos[n].assignees = JSON.parse(this.photos[n].players)
            } else {
              this.photos[n].assignees = []
            }
          }

          this.refreshPhotoSizing()

          this.default_aspect_ratio =  response.data.default_aspect_ratio
          this.confirm_before_delete = !!response.data.confirm_before_delete
          this.email_active = response.data.email_active
          endPhotosLoading()
        }).catch((err) => {
        console.log(err)
        endPhotosLoading()
        // A failed fetch leaves the previous photos on screen, so drop their
        // loaders rather than spinning forever.
        this.setPhotoSizing(false)
        if(err.response.status == 401){
          this.showAlert({
            title: 'Error',
            message: 'You do not have access to this feature. Reach out to your admin regarding your site permissions.',
            variant: 'danger'
          }).then((response)=> {
            this.$router.push('/')
          })
        }
        else if(err.response.status == 500){
          this.feature_not_enabled = true
        }
        else {
          this.showAlert({ title: 'Error', message: 'Something went wrong', variant: 'danger' })
        }
      })
    },
    takePhoto(){

      const fileInput = event.target;
      const file = fileInput.files[0];

      if (!file) {
        return;
      }

      if (!file.type.match('image.*')) {
        this.showAlert({ title: 'Error', message: 'You must upload an image', variant: 'danger' })
        return
      }

      this.file = file;

      if (this.review_before_save) {
        this.pendingPhoto = { url: URL.createObjectURL(file), file, inputEl: fileInput }
        return
      }
      // Now proceed with upload
      this.processPhotoUpload();
    },
    // Live overlay camera ---------------------------------------------------
    onTakePhotoClick() {
      // With the phone camera the file input inside the button handles the click.
      if (!this.useLiveCamera) return
      this.liveCamera = { show: true, overlayId: null }
    },
    closeLiveCamera() {
      this.liveCamera.show = false
    },
    useNativeCamera() {
      this.closeLiveCamera()
      this.$nextTick(() => this.$refs.nativeInput && this.$refs.nativeInput.click())
    },
    async onLiveCapture({ file, overlayId }) {
      this.closeLiveCamera()
      const overlay = overlayId ? this.overlays.find(o => o.id === overlayId) || null : null
      this.file = file
      if (this.review_before_save) {
        this.pendingPhoto = { url: URL.createObjectURL(file), file, inputEl: null, overlayId: overlay ? overlay.id : null, source: 'live' }
        return
      }
      // The live view already showed the overlay, so the choice made there stands in for auto apply.
      await this.processPhotoUpload({ overlay, overlayLayers: null })
    },
    // Review drawer (pending mode) -------------------------------------------
    cancelPending() {
      if (!this.pendingPhoto) return
      URL.revokeObjectURL(this.pendingPhoto.url)
      if (this.pendingPhoto.inputEl) this.pendingPhoto.inputEl.value = ''
      this.pendingPhoto = null
      this.file = ''
    },
    retakePending() {
      const pending = this.pendingPhoto
      this.cancelPending()
      if (!pending) return
      if (pending.source === 'live') {
        this.liveCamera = { show: true, overlayId: pending.overlayId || null }
      } else if (pending.inputEl) {
        pending.inputEl.click()
      }
    },
    async usePending({ overlay, overlayLayers }) {
      const pending = this.pendingPhoto
      if (!pending) return
      this.pendingPhoto = null
      this.file = pending.file
      await this.processPhotoUpload({ overlay, overlayLayers })
      URL.revokeObjectURL(pending.url)
      if (pending.inputEl) pending.inputEl.value = ''
    },
    // review: { overlay, overlayLayers } from the review drawer; the chosen
    // overlay is baked once the record exists, in place of the card's dropdown.
    async processPhotoUpload(review = null){

      if(!this.file){return}
      // Capture values immediately before any async operations to prevent race conditions
      // when localStorage changes due to group switching in another tab
      const companyId = localStorage.getItem('companyId');
      const groupId = JSON.parse(localStorage.getItem('group')).id;
      const t = new Date().toLocaleString( 'sv' );

      // Optimistic card: show the photo immediately from the local file with an
      // uploading scrim instead of blocking the whole page behind loading-panel.
      const placeholder = {
        uploading: true,
        isProcessing: false,
        isSizing: false,
        url: URL.createObjectURL(this.file),
        timestamp: new Date().toISOString(),
        assignees: [],
        selectedOverlay: null
      }
      this.photos.unshift(placeholder)
      this.refreshPhotoSizing()
      const dropPlaceholder = () => {
        const idx = this.photos.indexOf(placeholder)
        if (idx !== -1) this.photos.splice(idx, 1)
        URL.revokeObjectURL(placeholder.url)
        this.refreshPhotoSizing()
      }

      try {
        await this.saveFile()
      } catch (err) {
        dropPlaceholder()
        this.showAlert({ title: 'Error', message: 'Failed to upload photo. Please try again.', variant: 'danger' })
        return
      }
      if (!this.uploadUrl) {
        dropPlaceholder()
        return
      }
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + groupId + '/photos?t=' + t

      let data = {
        uploaded_image:  this.uploadUrl.split("?")[0],
        aspect_ratio: this.default_aspect_ratio
      }

      axios.post(url, data, config)
        .then(response => {
          this.file = ''
          this.uploadUrl = ''
          let newPhoto = response.data.photo; // Assuming your API returns the new photo
          newPhoto.isProcessing = false;
          newPhoto.assignees = [];
          newPhoto.selectedOverlay = null;
          // Swap the optimistic placeholder for the server photo in place.
          const idx = this.photos.indexOf(placeholder)
          if (idx !== -1) {
            this.photos.splice(idx, 1, newPhoto)
          } else {
            this.photos.unshift(newPhoto)
          }
          URL.revokeObjectURL(placeholder.url)
          this.refreshPhotoSizing()
          if (review && review.overlay) {
            newPhoto.selectedOverlay = review.overlay
            this.$set(newPhoto, 'overlay_layers', review.overlayLayers || null)
            this.saveOverlay(newPhoto, this.photos.indexOf(newPhoto))
          }
        }).catch((err) => {
        console.log(err)
        dropPlaceholder()
        this.handleApiError(err)
      })
    },
    async getUsersForBooking(booking_id, purchaserEmail = null){
      this.emailUsersLoading = true
      let token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/'
        + companyGroupId + '/bookings/' + booking_id + '/waivers'

      axios.get(url, config)
        .then(response => {
          this.emailUsersLoading = false
          const users = response.data.users || []
          if(purchaserEmail){
            // Child-linked photo: master's full waiver list returned; pre-select only waivers whose
            // signer email matches the purchaser's email (best-guess party attribution). Others remain visible + unchecked.
            this.end_users = users.map(u => ({ ...u, from_waiver: true, selected: u.email === purchaserEmail ? 1 : 0 }))
          } else {
            this.end_users = users.map(u => ({ ...u, from_waiver: true }))
          }
          // Seed purchasers not already covered by a signed waiver, pre-checked
          // when no waiver row is selected, so venues without waivers can send
          // without searching. Public slots return one purchaser per party; the
          // single purchaser from the photo row is the fallback for older data.
          let purchasers = response.data.purchasers || []
          if(purchasers.length === 0 && this.emailGroupPurchaser){
            purchasers = [this.emailGroupPurchaser]
          }
          const anySelected = this.end_users.some(u => u.selected)
          const seen = new Set(this.end_users.map(u => u.email))
          const seeds = []
          for(const pu of purchasers){
            if(!pu.email || seen.has(pu.email)) continue
            seen.add(pu.email)
            seeds.push({
              first_name: pu.first_name || '',
              last_name: pu.last_name || '',
              email: pu.email,
              end_user_id: pu.end_user_id,
              order_number: pu.order_number,
              opted_out_photo_sharing: pu.opted_out_photo_sharing,
              is_purchaser: true,
              selected: anySelected ? 0 : 1
            })
          }
          this.end_users = seeds.concat(this.end_users)
        }).catch((err) => {
        console.log(err)
        this.emailUsersLoading = false
        this.handleApiError(err)
      })
    },
    // Promise-based wrapper around the shared base-alert-dialog. Resolves true
    // on confirm, false on cancel or dismiss.
    showAlert(opts){
      if(this.alertResolve){
        this.alertResolve(false)
      }
      this.alertDialog = {
        show: true,
        title: opts.title || '',
        message: opts.message || '',
        variant: opts.variant || 'warning',
        showCancel: opts.showCancel === true,
        confirmText: opts.confirmText || 'OK',
        cancelText: opts.cancelText || 'Cancel',
        confirmVariant: opts.confirmVariant || (opts.showCancel && opts.variant === 'warning' ? 'danger' : 'primary')
      }
      return new Promise(resolve => { this.alertResolve = resolve })
    },
    // Shared handler for the standard axios error branches. 401 sends the
    // operator back home; anything else gets an honest generic message (the
    // old copy blamed the subscription plan for every 500).
    onPageEscKey(e){
      if(e.key !== 'Escape') return
      // base-alert-dialog handles its own ESC; only act when no dialog is up.
      if(this.alertDialog.show || this.sizeDialog.show) return
      if(this.drawerOpen){
        this.closeAssignmentDrawer()
      } else if(this.modals.email_group){
        this.modals.email_group = false
      }
    },
    handleApiError(err){
      const status = err && err.response && err.response.status
      if(status === 401){
        this.showAlert({
          title: 'Error',
          message: 'You do not have access to this feature. Reach out to your admin regarding your site permissions.',
          variant: 'danger'
        }).then(() => {
          this.$router.push('/')
        })
      } else {
        this.showAlert({ title: 'Error', message: 'Something went wrong. Please try again.', variant: 'danger' })
      }
    },
    resolveAlert(value){
      this.alertDialog.show = false
      const resolve = this.alertResolve
      this.alertResolve = null
      if(resolve) resolve(value)
    },
    async emailToGroupPrompt(assigneeRow){
      if(!this.email_active){
        return
      }
      this.selectedPhoto = assigneeRow

      if(assigneeRow.hasOwnProperty('user_id')){
        this.selectedPhoto.link_type = 0

        // Find all photos assigned to this same customer
        const matchingPhotos = this.photos.filter(p =>
          p.assignees && p.assignees.some(a => a.user_id === assigneeRow.user_id)
        ).map(p => ({
          ...p,
          selected: true,
          _linkId: p.assignees.find(a => a.user_id === assigneeRow.user_id)?.id
        }))

        if(matchingPhotos.length <= 1){
          // Single photo: preserve current instant-send behavior
          this.emailPhotoCandidates = []
          this.$set(assigneeRow, 'selected', true)
          this.end_users = []
          this.end_users.push(assigneeRow)
          this.emailToGroup()
        } else {
          // Multiple photos: show selection modal
          this.emailPhotoCandidates = matchingPhotos
          this.emailCustomerTarget = assigneeRow
          this.modals.email_customer_photos = true
          this.ensureCandidateSizes()
        }
      } else {
        this.selectedPhoto.link_type = 1

        // Find all photos assigned to this same booking
        this.emailPhotoCandidates = this.photos.filter(p =>
          p.assignees && p.assignees.some(a => a.booking_slot_id === assigneeRow.booking_slot_id)
        ).map(p => ({
          ...p,
          selected: true,
          _linkId: p.assignees.find(a => a.booking_slot_id === assigneeRow.booking_slot_id)?.id
        }))

        this.end_users = []
        this.modals.email_group = true
        this.ensureCandidateSizes()
        // Purchaser comes from the booking itself, so venues that do not use
        // waivers still get a ready-made recipient (seeded in getUsersForBooking).
        this.emailGroupPurchaser = assigneeRow.purchaser_email ? {
          first_name: assigneeRow.purchaser_first_name || '',
          last_name: assigneeRow.purchaser_last_name || '',
          email: assigneeRow.purchaser_email
        } : null
        this.emailGroupPurchaserEmail = assigneeRow.purchaser_email || null
        // Child-linked photo: query the master's whole waiver list, then pre-select waivers
        // matching the purchaser's email. Master/private: query the linked slot as today.
        if(assigneeRow.master_slot_id){
          this.getUsersForBooking(assigneeRow.master_slot_id, assigneeRow.purchaser_email)
        } else {
          this.getUsersForBooking(assigneeRow.booking_slot_id)
        }
      }
    },
    async emailCustomerPhotos(){
      this.$set(this.emailCustomerTarget, 'selected', true)
      this.end_users = [this.emailCustomerTarget]
      await this.emailToGroup()
    },
    async emailToGroup(options = {}){
      let selectedPhotos = this.emailPhotoCandidates.filter(p => p.selected)
      if(this.emailPhotoCandidates.length > 0 && selectedPhotos.length === 0){
        return
      }

      // No recipients selected (or the waiver list is still loading): sending
      // would post an empty emails array, which the lambda treats as success.
      let selectedRecipients = this.end_users.filter(u => u.selected)
      if(selectedRecipients.length === 0){
        this.showAlert({
          title: 'No recipients selected',
          message: 'Select at least one recipient, or use "Add another person" to send to a specific customer.',
          variant: 'warning'
        })
        return
      }

      // Opt-in confirmation: warn (allow override) if any selected recipient opted out of photo sharing.
      let optedOut = selectedRecipients.filter(u => u.opted_out_photo_sharing)
      if(optedOut.length > 0){
        let isSingle = selectedRecipients.length === 1
        let names = optedOut
          .map(u => `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email)
          .join(', ')
        let confirmed = await this.showAlert({
          title: isSingle ? 'Customer opted out' : 'Some participants opted out',
          message: isSingle
            ? `This customer opted out of having their photo shared. Only send if you have their consent.`
            : `${names} opted out of having their photo shared. Only send if you have their consent.`,
          variant: 'warning',
          showCancel: true,
          confirmText: 'Send anyway',
          cancelText: 'Cancel'
        })
        if(confirmed !== true){
          return
        }
      }

      this.sendingEmail = true
      this.loading = true
      var token = await this.getAuthToken()
      let config = {headers: {Authorization: token}}
      let companyId = localStorage.getItem('companyId')
      let companyGroupId = JSON.parse(localStorage.getItem('group')).id
      let photoIdForUrl = selectedPhotos.length > 0 ? selectedPhotos[0].id : this.selectedPhoto.photo_id
      let url = API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId + '/photos/'+ photoIdForUrl + '/email-group'

      let emails = []
      for(let n in this.end_users){
        if(this.end_users[n].selected){
          emails.push(this.end_users[n])
        }
      }
      let data = {
        link_type: this.selectedPhoto.link_type,
        link_id: this.selectedPhoto.id,
        emails: emails,
      }

      if(options.allowLinks){
        data.allow_links = true
      }

      // Add multi-photo data if available
      if(selectedPhotos.length > 0){
        data.photoIds = selectedPhotos.map(p => p.id)
        data.link_ids = selectedPhotos.map(p => p._linkId).filter(id => id != null)
      }

      axios.post(url, data, config)
        .then(response => {
          this.loading = false
          this.sendingEmail = false
          let count = selectedPhotos.length || 1
          this.$notify({
            type: 'success',
            message: count === 1 ? 'The photo was emailed to players.' : `${count} photos were emailed to players.`,
            icon: 'tim-icons icon-check-2'
          })
          this.modals.email_group = false
          this.modals.email_customer_photos = false
          this.getPhotos()
        }).catch((err) => {
        console.log(err)
        this.loading = false
        this.sendingEmail = false
        if(err.response.status == 401){
          this.showAlert({
            title: 'Error',
            message: 'You do not have access to this feature. Reach out to your admin regarding your site permissions.',
            variant: 'danger'
          }).then((response)=> {
            this.$router.push('/')
          })
        }
        else if(isNoRecipientsError(err)){
          this.showAlert({ title: 'No one to email', message: NO_RECIPIENTS_MESSAGE, variant: 'warning' })
        }
        else if(err.response.status == 413){
          let errorData = err.response.data
          if(typeof errorData === 'string'){
            try { errorData = JSON.parse(errorData) } catch(e) { errorData = {} }
          }
          let sizes = new Map((errorData.photo_sizes || []).map(p => [p.id, p.mb]))
          for(let i = 0; i < this.emailPhotoCandidates.length; i++){
            this.$set(this.emailPhotoCandidates[i], 'size_mb', sizes.get(this.emailPhotoCandidates[i].id) || null)
          }
          this.sizeDialog = { show: true, totalMb: errorData.total_mb || 0, limitMb: errorData.limit_mb || 18 }
        }
        else if(err.response.status == 500){
          this.showAlert({ title: 'Error', message: 'Something went wrong. Please try again.', variant: 'danger' })
        }
        else {
          this.showAlert({ title: 'Error', message: 'Something went wrong', variant: 'danger' })
        }
      })
    },
    setPhotoSizing(value){
      for(let n in this.photos){
        this.$set(this.photos[n], 'isSizing', value)
      }
    },
    // Marks every photo as sizing, then immediately clears the ones whose <img>
    // node is already loaded. Nodes that really are fetching a new src stay
    // covered by the loader until their @load fires. Call after any change to
    // the photos array, since the grid is keyed by index and shifting the array
    // repoints existing <img> nodes at a different photo.
    refreshPhotoSizing(){
      this.setPhotoSizing(true)
      this.$nextTick(() => {
        for(let i = 0; i < this.photos.length; i++){
          const img = document.getElementById('img' + i)
          if(img && img.complete && img.naturalWidth > 0){
            this.updateCanvasSize(i, this.photos[i])
          }
        }
      })
    },
    photoLoadFailed(item){
      // Never leave the loader spinning on a broken image.
      this.$set(item, 'isSizing', false)
    },
    // The grid displays every photo at full size, so the bytes are already in
    // the HTTP cache; force-cache makes this a free local read, not a download.
    async loadPhotoSize(photo){
      if(!photo || photo.size_bytes || photo.uploading) return
      try {
        const source = photo.overlay_url || photo.url
        const res = await fetch(source, { cache: 'force-cache', mode: 'cors' })
        if(!res.ok) return
        const blob = await res.blob()
        this.$set(photo, 'size_bytes', blob.size)
        this.$set(photo, 'size_mb', Number((blob.size / (1024 * 1024)).toFixed(1)))
      } catch(e) {
        // Size display is best-effort; the backend gate stays authoritative.
      }
    },
    ensureCandidateSizes(){
      for(const c of this.emailPhotoCandidates){
        if(!c.size_bytes){
          this.loadPhotoSize(c)
        }
      }
    },
    updateCanvasSize(index, item) {
      //Get underlying image parameters
      let underlyingImage = document.getElementById('img' + index)

      if(!underlyingImage){
        this.$set(item, 'isSizing', false)
        return
      }

      // The grid is keyed by index, so this same <img> node is reused when the
      // date changes or when an overlay swaps the src. Clear any width set by a
      // previous load before measuring, otherwise each load halves the already
      // halved width and portrait photos shrink on every date change.
      underlyingImage.removeAttribute('width')

      if(underlyingImage.height > underlyingImage.width){
        underlyingImage.width = underlyingImage.width * 0.5
      }

      let imgHeight = underlyingImage.height
      let imgWidth = underlyingImage.width

      //Set up canvas
      const canvas = document.getElementById('cv' + index);
      let ctx = canvas.getContext('2d');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      canvas.height = imgHeight
      canvas.width = imgWidth

      let scale = window.devicePixelRatio;
      let originalWidth = canvas.offsetWidth;
      let originalHeight = canvas.offsetHeight;

      canvas.width = originalWidth * scale;
      canvas.height = originalHeight * scale;

      canvas.style.width = originalWidth + 'px';
      canvas.style.height = originalHeight + 'px';

      ctx.scale(scale, scale);

      // Sizing is done, reveal the photo.
      this.$set(item, 'isSizing', false)
    },
    // Download hands back the exact file stored in S3 (the baked composite when
    // an overlay is applied, else the original). No canvas re-encode: that
    // changed the bytes, inflated the size, and stamped the overlay a second
    // time on already-baked photos.
    // Hands a blob to the browser as a file download. Shared by the single photo
    // download and the day zip.
    triggerBlobDownload(blob, fileName) {
      const link = document.createElement('a')
      link.download = fileName
      link.href = URL.createObjectURL(blob)
      link.click()
      URL.revokeObjectURL(link.href)
    },
    async saveImage(item) {
      const source = item.overlay_url || item.url
      try {
        const response = await fetch(source, { mode: 'cors', cache: 'no-store' })
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        const blob = await response.blob()
        const timestamp = new Date().toISOString().slice(0, 19).replace(/[:.]/g, '-')
        this.triggerBlobDownload(blob, `photo_${timestamp}.jpg`)
      } catch (err) {
        console.error('Photo download failed', err)
        this.showAlert({ title: 'Error', message: 'The photo could not be downloaded. Please try again.', variant: 'danger' })
      }
    },
    // Fetches every photo of the selected day, packs them into zip parts of at
    // most PART_BUDGET_BYTES and downloads each part as soon as it is full, so
    // memory stays near one part regardless of how big the day is.
    async downloadAllPhotos() {
      if (this.zipState.running || !Array.isArray(this.photos) || this.photos.length === 0) return
      const day = this.current
      const photos = this.photos.filter(p => !p.uploading)
      if (photos.length === 0) return

      const knownBytes = photos.map(p => p.size_bytes || null)
      const knownTotal = knownBytes.reduce((sum, b) => sum + (b || 0), 0)
      if (knownTotal > PART_BUDGET_BYTES) {
        const parts = estimatePartCount(knownBytes)
        const totalMb = Math.round(knownTotal / (1024 * 1024))
        const ok = await this.showAlert({
          title: 'Large download',
          message: `These photos add up to about ${totalMb} MB, so they arrive as ${parts} zip files of up to ${Math.round(PART_BUDGET_BYTES / (1024 * 1024))} MB each.`,
          variant: 'info',
          showCancel: true,
          confirmText: 'Download',
          cancelText: 'Cancel',
          confirmVariant: 'primary'
        })
        if (!ok) return
      }

      this.zipState = { running: true, done: 0, total: photos.length }
      let entries = []
      let partBytes = 0
      let partIndex = 0
      let failed = 0
      let saved = 0
      const multiPart = estimatePartCount(knownBytes) > 1

      const flushPart = async () => {
        if (entries.length === 0) return
        partIndex += 1
        const blob = await buildZipBlob(entries)
        this.triggerBlobDownload(blob, partFileName(day, partIndex, multiPart || partIndex > 1))
        saved += entries.length
        entries = []
        partBytes = 0
      }

      try {
        for (const photo of photos) {
          const source = photo.overlay_url || photo.url
          let bytes = null
          try {
            const response = await fetch(source, { mode: 'cors', cache: 'no-store' })
            if (!response.ok) throw new Error(`HTTP ${response.status}`)
            bytes = new Uint8Array(await response.arrayBuffer())
          } catch (err) {
            console.error('Photo fetch failed', photo.id, err)
            failed += 1
          }
          this.zipState.done += 1
          if (!bytes) continue
          if (startsNewPart(partBytes, entries.length, bytes.length)) {
            await flushPart()
          }
          const mtime = new Date(photo.timestamp)
          entries.push({ name: photoFileName(photo), bytes, mtime: isNaN(mtime.getTime()) ? undefined : mtime })
          partBytes += bytes.length
        }
        await flushPart()
      } catch (err) {
        console.error('Photo zip failed', err)
        this.showAlert({ title: 'Error', message: 'The photos could not be downloaded. Please try again.', variant: 'danger' })
        return
      } finally {
        this.zipState = { running: false, done: 0, total: 0 }
      }

      if (saved === 0) {
        this.showAlert({ title: 'Error', message: 'None of the photos could be downloaded. Please try again.', variant: 'danger' })
      } else if (failed > 0) {
        const noun = failed === 1 ? 'photo' : 'photos'
        this.showAlert({ title: 'Some photos were skipped', message: `${failed} ${noun} could not be downloaded and ${failed === 1 ? 'was' : 'were'} left out of the zip. Try again later for ${failed === 1 ? 'that one' : 'those'}.`, variant: 'warning' })
      }
    }
  },
  beforeDestroy(){
    document.removeEventListener('keydown', this.onPageEscKey)
  },
  async mounted(){
    document.addEventListener('keydown', this.onPageEscKey)
    this.skeletonTimer = setTimeout(() => {
      if(this.photosLoading) this.skeletonsVisible = true
    }, 200)
    await this.getOverlays()
    this.setToday()
  }
}
</script>

<style scoped>

.tp-date-nav-wrap {
  display: flex;
  justify-content: flex-end;
}
@media (max-width: 575.98px) {
  .tp-date-nav-wrap {
    justify-content: center;
  }
}

/* Per photo loading state. Matches the lightbox loader in GameInstance.vue:
   an inset scrim with the app wide custom-spinner from LoadingPanel. */
.tp-photo-loader {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.3);
  z-index: 9;
}

canvas {
  image-rendering: optimizeQuality;
}

.tp-photo-thumbs {
  display: flex;
  gap: 4px;
  overflow-x: auto;
  padding: 8px 0 4px;
}
.tp-photo-thumb {
  position: relative;
  flex-shrink: 0;
  width: 56px;
  height: 56px;
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid transparent;
  opacity: 0.6;
  transition: opacity 0.15s, border-color 0.15s;
}
.tp-photo-thumb__size {
  position: absolute;
  left: 2px;
  bottom: 2px;
  font-size: 9px;
  padding: 1px 4px;
}
.tp-photo-thumb:hover {
  opacity: 0.85;
}
.tp-photo-thumb--selected {
  border-color: #e14eca;
  opacity: 1;
}
.tp-photo-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.tp-photo-thumb__check {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 20px;
  height: 20px;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.7);
  border: 2px solid rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}
.tp-photo-thumb__check:hover {
  border-color: #e14eca;
}
.tp-photo-thumb__check i {
  font-size: 12px;
  color: #fff;
  font-weight: bold;
}
.tp-photo-thumb__check--selected {
  background: #e14eca;
  border-color: #e14eca;
}
/* Picker list */
.tp-picker-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.tp-picker-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 8px;
  cursor: pointer;
  min-height: 56px;
  transition: background 0.12s ease;
}
.tp-picker-row:hover:not(.tp-picker-row--selected) {
  background: rgba(255, 255, 255, 0.02);
}
.tp-picker-row--selected {
  background: rgba(225, 78, 202, 0.08);
}
.tp-picker-row__time {
  flex-shrink: 0;
  min-width: 44px;
  font-size: 13px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
}

/* Event cell layout (shared between tabs) */
.tp-picker-event {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
  position: relative;
}
.tp-picker-event__thumb-wrap {
  position: relative;
  width: 36px;
  height: 36px;
  flex-shrink: 0;
}
.tp-picker-event__img {
  width: 36px;
  height: 36px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  display: block;
}
.tp-picker-event__img--placeholder {
  background: rgba(255, 255, 255, 0.04);
}
/* On touch devices (no real hover), keep the checkbox visible at low opacity so
   multi-select is discoverable without a tap. */
@media (hover: none) {
  .tp-picker-event__check {
    opacity: 0.5;
  }
  .tp-picker-event__check.is-checked {
    opacity: 1;
  }
}
.tp-picker-event__thumb-wrap.is-selected .tp-picker-event__img,
.tp-picker-event__thumb-wrap.is-selected .tp-participant-avatar {
  box-shadow: 0 0 0 2px #e14eca;
  border-color: transparent;
}
.tp-picker-event__check {
  position: absolute;
  bottom: -5px;
  right: -5px;
  width: 18px;
  height: 18px;
  border-radius: 4px;
  background: #171826;
  border: 2px solid rgba(255, 255, 255, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.12s ease, background 0.12s ease, border-color 0.12s ease;
  box-sizing: border-box;
}
.tp-picker-event__check i {
  font-size: 10px;
  color: transparent;
  line-height: 1;
}
.tp-picker-event__check.is-checked {
  opacity: 1;
  background: #e14eca;
  border-color: #e14eca;
}
.tp-picker-event__check.is-checked i {
  color: #fff;
}
.tp-picker-row:hover .tp-picker-event__check {
  opacity: 1;
}
.tp-picker-event__text {
  flex: 1;
  min-width: 0;
  word-break: normal;
  overflow-wrap: break-word;
}
.tp-picker-event__text small {
  word-break: normal;
  overflow-wrap: break-word;
}
.tp-picker-event__status {
  margin-left: auto;
  flex-shrink: 0;
  line-height: 1;
}
/* Assignee list on the photo card — mirrors the drawer picker row style but without hover/selection chrome */
.tp-assignee-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 8px;
}
.tp-assignee-row {
  padding: 8px 4px;
  border-radius: 6px;
}
.tp-assignee-row__status {
  margin-left: auto;
  flex-shrink: 0;
}
.tp-assignee-row__actions {
  flex-shrink: 0;
}
.tp-assignee-row__kebab {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.55);
  cursor: pointer;
  min-width: 40px;
  min-height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: color 0.12s ease, background 0.12s ease;
}
.tp-assignee-row__kebab:hover {
  color: #e14eca;
  background: rgba(225, 78, 202, 0.1);
}
.tp-assignee-row__kebab i {
  font-size: 18px;
}
.tp-participant-avatar {
  width: 36px;
  height: 36px;
  border-radius: 6px;
  background: rgba(225, 78, 202, 0.15);
  color: #e14eca;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  border: 1px solid rgba(255, 255, 255, 0.08);
  letter-spacing: 0.02em;
}
.tp-participant-remove {
  margin-left: auto;
  flex-shrink: 0;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.4);
  cursor: pointer;
  min-width: 40px;
  min-height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: color 0.12s ease, background 0.12s ease;
}
.tp-participant-remove i {
  font-size: 12px;
}
.tp-participant-remove:hover {
  color: #f5365c;
  background: rgba(245, 54, 92, 0.1);
}
.tp-picker-show-all {
  display: flex;
  justify-content: center;
  padding: 8px 0 4px;
}
@media (max-width: 480px) {
  .tp-mode-toggle ::v-deep .toggle-group {
    display: flex;
    width: 100%;
  }
  .tp-mode-toggle ::v-deep .toggle-option {
    flex: 1;
    justify-content: center;
  }
}
/* Flex-center the CTA content: tim-icons have their own line box, so
   baseline alignment always reads off against the label's cap height. */
.tp-take-photo-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.tp-take-photo-btn .tim-icons {
  /* The camera glyph is drawn small and low in its em box; scale up and lift
     to optical center against the bold label. */
  font-size: 1.15em;
  line-height: 1;
  transform: translateY(-1px);
}
.tp-upload-skel {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  padding-top: 16px;
}
.tp-upload-skel__pill {
  width: 130px;
  height: 30px;
  border-radius: 999px;
}
.tp-upload-skel__label {
  width: 45%;
  height: 12px;
}
.tp-upload-skel__select {
  width: 100%;
  height: 38px;
  border-radius: 0.4285rem;
}
.tp-skel-photo {
  width: 100%;
  height: 180px;
  border-radius: 4px;
}
.tp-overlay-row {
  display: flex;
  align-items: flex-end;
  gap: 2px;
  margin-top: 12px;
}
.tp-overlay-field { flex: 1; min-width: 0; margin-right: 6px; }
/* Same bare icon buttons as the date row's delete and download, at the theme's default
   size, centered against the select's height. */
.tp-overlay-actions {
  display: flex;
  align-items: center;
  gap: 2px;
  height: calc(2.25rem + 2px);
}
.tp-overlay-action { margin: 0; }
.tp-overlay-action i { opacity: 0.7; }
.tp-overlay-action:hover i { opacity: 1; }
.tp-badge-sm {
  font-size: 10px;
}
.tp-row-title {
  font-weight: 600;
}
.tp-row-sub {
  opacity: 0.7;
}
@media (max-width: 576px) {
  .footer-actions {
    width: 100%;
  }
  .footer-actions ::v-deep .btn {
    flex: 1;
  }
}
.tp-picker-show-all__btn {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.55);
  font-size: 12px;
  padding: 6px 10px;
  cursor: pointer;
  border-radius: 6px;
  transition: color 0.12s ease, background 0.12s ease;
}
.tp-picker-show-all__btn:hover {
  color: #e14eca;
  background: rgba(225, 78, 202, 0.08);
}
.tp-drawer-photo-ref__thumb {
  width: 48px;
  height: 48px;
  object-fit: cover;
  border-radius: 6px;
  flex-shrink: 0;
  border: 1px solid rgba(225, 78, 202, 0.3);
}
.tp-drawer-photo-ref__thumb--placeholder {
  background: rgba(255, 255, 255, 0.04);
}
.tp-drawer-photo-ref__avatar {
  width: 48px;
  height: 48px;
  font-size: 14px;
  border-radius: 6px;
}
::v-deep .base-drawer__header {
  padding-top: 14px;
  padding-bottom: 14px;
}
</style>

<style>
/* Unscoped: reaches inside base-date-navigator. Page-local via the wrapper
   class. On phones the navigator fills the row instead of shrinking to its
   minimum beside the camera button. */
@media (max-width: 575.98px) {
  .tp-date-nav-wrap .base-date-navigator {
    width: 100%;
  }
  .tp-date-nav-wrap .base-date-navigator__picker {
    flex: 1 1 auto;
    min-width: 0;
  }
}
.white-content .tp-picker-event__img {
  border-color: rgba(0, 0, 0, 0.1);
}
.white-content .tp-picker-event__img--placeholder {
  background: rgba(0, 0, 0, 0.04);
}
.white-content .tp-picker-row--selected {
  background: rgba(225, 78, 202, 0.07);
}
.white-content .tp-picker-row:hover:not(.tp-picker-row--selected) {
  background: rgba(0, 0, 0, 0.02);
}
.white-content .tp-picker-row__time {
  color: rgba(0, 0, 0, 0.75);
}
.white-content .tp-picker-event__check {
  background: #ffffff;
  border-color: rgba(0, 0, 0, 0.25);
}
.white-content .tp-picker-event__check.is-checked {
  background: #e14eca;
  border-color: #e14eca;
}
.white-content .tp-participant-avatar {
  background: rgba(225, 78, 202, 0.12);
  border-color: rgba(0, 0, 0, 0.08);
}
.white-content .tp-assignee-row__kebab {
  color: rgba(0, 0, 0, 0.55);
}
.white-content .tp-assignee-row__kebab:hover {
  color: #b13aa0;
  background: rgba(225, 78, 202, 0.1);
}
.white-content .tp-participant-remove {
  color: rgba(0, 0, 0, 0.45);
}
.white-content .tp-picker-show-all__btn {
  color: rgba(0, 0, 0, 0.55);
}
.white-content .tp-picker-show-all__btn:hover {
  color: #b13aa0;
  background: rgba(225, 78, 202, 0.08);
}
</style>
